import { api } from "./client";
import type { UserInfo } from "@/store/auth";

// ---------- Common types ----------
export interface PaginatedList<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// ---------- Auth ----------
export interface LoginRequest { email: string; password: string; branchId?: string | null }
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: UserInfo;
}

export const authApi = {
  login: (req: LoginRequest) =>
    api.post<LoginResponse>("/api/v1/auth/login", req).then(r => r.data),
  logout: () => api.post("/api/v1/auth/logout"),
};

// ---------- Items ----------
export interface ItemDto {
  id: string;
  sku: string;
  name: string;
  genericName?: string;
  itemType: string;
  hsnCode?: string;
  baseUomId: string;
  costingMethod: string;
  reorderLevel: number;
  reorderQty: number;
  minOrderQty?: number;
  safetyStock: number;
  leadTimeDays: number;
  isBatchTracked: boolean;
  isExpiryTracked: boolean;
  form?: string;
  packaging?: string;
  strength?: string;
  manufacturer?: string;
  category?: string;
  defaultMrp?: number;
  gstRate: number;
  isScheduleDrug: boolean;
  scheduleCategory?: string;
  barcode?: string;
  /** Storage / refrigeration mode: RoomTemperature | Cool | Refrigerated | Frozen. */
  storageCondition?: string;
  isActive?: boolean;
  // Medicine clinical attributes (ABC/VED classification, route, ICD, high-alert, remarks).
  /** ABC value classification: A | B | C. */
  abcClass?: string | null;
  /** VED criticality: Vital | Essential | Desirable. */
  vedClass?: string | null;
  /** Route of administration — a name from the Item Routes master, e.g. Oral | Intravenous. */
  route?: string | null;
  /** ICD code for the associated condition. */
  icdCode?: string | null;
  /** High-alert medication flag. */
  isHighAlert?: boolean;
  /** Free-text remarks / notes. */
  remarks?: string | null;
  /** FK to the generic-name master (medicines); genericName holds the display copy. */
  genericNameId?: string | null;
  /** Alternate units of measure (pack sizes). Returned by GET /items/{id}; sent on create/update. */
  alternateUoms?: ItemUomInput[];
}

/** Alternate UoM row: how many base units the alternate unit holds (e.g. Box = 10). */
export interface ItemUomInput {
  uomId: string;
  conversionFactor: number;
  /** Present on GET responses for display; ignored on write. */
  uomCode?: string;
  uomName?: string;
}

export interface SubstituteOptionDto {
  itemId: string; sku: string; name: string; genericName?: string;
  form?: string; strength?: string; manufacturer?: string;
  priority: number;
  reasonCode: string;
  requiresPharmacistConfirm: boolean;
  /** Dispensable now — total less expired less reserved. This is the number that matters. */
  availableQty: number; mrp?: number;
  /** Linked generic master row; drives the monograph pop-out. Absent = nothing to show. */
  genericNameId?: string | null;
  /** Breakdown explaining the gap between shelf count and dispensable stock. */
  totalQty?: number; expiredQty?: number; reservedQty?: number;
}

// ---------- Item substitutes master ----------
export type SubstituteReasonCode = "SameApi" | "Generic" | "TherapeuticEquiv" | "Formulary";

export interface ItemSubstituteDto {
  id: string;
  primaryItemId: string; primaryItemSku: string; primaryItemName: string; primaryGenericName?: string | null;
  substituteItemId: string; substituteItemSku: string; substituteItemName: string; substituteGenericName?: string | null;
  priority: number;
  reasonCode: SubstituteReasonCode;
  requiresPharmacistConfirm: boolean;
  isActive: boolean;
  /** Both items resolve to the same generic master row — the safe case. */
  sameGeneric: boolean;
  /** The substitute item has been deactivated, so this pair offers nothing. */
  substituteInactive: boolean;
}

export interface CreateItemSubstitutePayload {
  primaryItemId: string; substituteItemId: string;
  priority: number;
  reasonCode: SubstituteReasonCode;
  requiresPharmacistConfirm: boolean;
  /** Also write the mirror pair, so the two items substitute each other. */
  alsoReverse: boolean;
}

export interface UpdateItemSubstitutePayload {
  priority: number;
  reasonCode: SubstituteReasonCode;
  requiresPharmacistConfirm: boolean;
  isActive: boolean;
}

export const itemSubstitutesApi = {
  list: (params: {
    search?: string; primaryItemId?: string; isActive?: boolean;
    crossGenericOnly?: boolean; page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<ItemSubstituteDto>>("/api/v1/item-substitutes", { params }).then(r => r.data),
  create: (payload: CreateItemSubstitutePayload) =>
    api.post<{ primaryItemId: string }>("/api/v1/item-substitutes", payload).then(r => r.data),
  update: (id: string, payload: UpdateItemSubstitutePayload) =>
    api.put(`/api/v1/item-substitutes/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/item-substitutes/${id}`),
};

export const itemsApi = {
  list: (params: { search?: string; itemType?: string; category?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ItemDto>>("/api/v1/items", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<ItemDto>(`/api/v1/items/${id}`).then(r => r.data),
  create: (payload: Partial<ItemDto>) =>
    api.post<{ id: string }>("/api/v1/items", payload).then(r => r.data),
  update: (id: string, payload: Partial<ItemDto>) =>
    api.put(`/api/v1/items/${id}`, { id, ...payload }),
  delete: (id: string) =>
    api.delete(`/api/v1/items/${id}`),
  substitutes: (itemId: string, warehouseId: string) =>
    api.get<SubstituteOptionDto[]>(`/api/v1/items/${itemId}/substitutes`, { params: { warehouseId } }).then(r => r.data),
  confusables: (itemId: string) =>
    api.get<ConfusableDto[]>(`/api/v1/items/${itemId}/confusables`).then(r => r.data),
};

// ---------- Look-alike / sound-alike (LASA) ----------
export type ConfusionType = "SoundAlike" | "LookAlike" | "Both";
export type LasaSeverity = "Low" | "Medium" | "High";

export interface ConfusableDto {
  partnerItemId: string; partnerSku: string; partnerName: string;
  confusionType: ConfusionType; severity: LasaSeverity; notes?: string | null;
}

export interface LasaPairDto {
  id: string;
  itemAId: string; itemASku: string; itemAName: string;
  itemBId: string; itemBSku: string; itemBName: string;
  confusionType: ConfusionType; severity: LasaSeverity; notes?: string | null; isActive: boolean;
}

export interface LasaPairPayload {
  itemAId?: string; itemBId?: string;
  confusionType: ConfusionType; severity: LasaSeverity; notes?: string | null; isActive?: boolean;
  /** The group this one sits under (W23). Null keeps it at the top level. */
  parentId?: string | null;
}

export const lasaApi = {
  list: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<LasaPairDto>>("/api/v1/lasa-pairs", { params }).then(r => r.data),
  create: (payload: LasaPairPayload) =>
    api.post<{ id: string }>("/api/v1/lasa-pairs", payload).then(r => r.data),
  update: (id: string, payload: LasaPairPayload) =>
    api.put(`/api/v1/lasa-pairs/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/lasa-pairs/${id}`),
};

// ---------- Vendors / Customers ----------
export interface VendorDto {
  id: string; code: string; name: string;
  gstin?: string; pan?: string; isTaxRegistered?: boolean; contactPerson?: string;
  phone?: string; email?: string;
  /** Serialised address object (JSON string), consistent with Customer/Branch. */
  addressJson?: string | null;
  paymentTerms?: string; creditDays?: number; isActive: boolean;
  /** Supply category (Medicine/Consumables/All) master reference. */
  supplyTypeId?: string | null;
  supplyTypeName?: string | null;
  /** Payable account head (AP sub-ledger) this vendor maps to. */
  accountId?: string | null;
  accountCode?: string | null;
  accountName?: string | null;
}

// ---------- Vendor-item links + grading (auto-suggest) ----------
export interface VendorItemDto {
  id: string;
  vendorId: string; vendorName: string;
  itemId: string; itemSku: string; itemName: string;
  lastRate?: number | null;
  leadTimeDays?: number | null;
  vendorSku?: string | null;
  rateScore: number;
  deliverySpeedScore: number;
  deliveryQualityScore: number;
  /** Computed server-side: average of graded criteria. */
  overallScore: number;
  isPreferred: boolean;
  isActive: boolean;
}

export interface VendorItemPayload {
  vendorId?: string; itemId?: string;
  lastRate?: number | null;
  leadTimeDays?: number | null;
  vendorSku?: string | null;
  rateScore: number;
  deliverySpeedScore: number;
  deliveryQualityScore: number;
  isPreferred: boolean;
  isActive?: boolean;
}

export const vendorItemsApi = {
  list: (params: { vendorId?: string; itemId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<VendorItemDto>>("/api/v1/vendor-items", { params }).then(r => r.data),
  suggest: (itemId: string) =>
    api.get<VendorItemDto[]>("/api/v1/vendor-items/suggest", { params: { itemId } }).then(r => r.data),
  create: (payload: VendorItemPayload) =>
    api.post<{ id: string }>("/api/v1/vendor-items", payload).then(r => r.data),
  update: (id: string, payload: VendorItemPayload) =>
    api.put(`/api/v1/vendor-items/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/vendor-items/${id}`),
};

/**
 * Where a defaulted purchase rate came from. Mirrors `PurchaseRateSource` on the server.
 * `ItemDefaultMrp` is the last-resort tier — the retail price, not a purchase rate — so the
 * entry screens flag it rather than presenting it as a negotiated figure.
 */
export type PurchaseRateSource =
  | "None" | "VendorRate" | "VendorHistory" | "ItemHistory" | "ItemDefaultMrp";

export interface PurchaseRateDto {
  itemId: string;
  rate: number;
  source: PurchaseRateSource;
  vendorId?: string | null;
  vendorName?: string | null;
  asOf?: string | null;
}

export const procurementPricingApi = {
  /** Resolve the rate a PO / direct-PI line should default to for this vendor + item. */
  purchaseRate: (params: { itemId: string; vendorId?: string }) =>
    api.get<PurchaseRateDto>("/api/v1/procurement/purchase-rate", { params })
      .then(r => r.data),
};

export interface CustomerDto {
  id: string; code: string; name: string;
  phone?: string; email?: string; uhid?: string;
  creditLimit?: number; isActive: boolean;
  /** India GSTIN (GST regime). */
  gstin?: string | null;
  /** Regime-neutral tax registration (VAT regime — UAE TRN, KSA TRN, UK VAT). */
  taxRegistrationNumber?: string | null;
  /**
   * Standing billing dimensions. These seed a new bill so a returning patient isn't re-keyed;
   * the bill keeps its own copy, so changing them here never alters an existing invoice.
   */
  admissionTypeId?: string | null;
  personTypeId?: string | null;
  insuranceSchemeId?: string | null;
  insurancePolicyNo?: string | null;
}

export const vendorsApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<VendorDto>>("/api/v1/vendors", { params }).then(r => r.data),
  get: (id: string) => api.get<VendorDto>(`/api/v1/vendors/${id}`).then(r => r.data),
  create: (payload: Partial<VendorDto>) =>
    api.post<{ id: string }>("/api/v1/vendors", payload).then(r => r.data),
  update: (id: string, payload: Partial<VendorDto>) =>
    api.put(`/api/v1/vendors/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/vendors/${id}`),
};

export const customersApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<CustomerDto>>("/api/v1/customers", { params }).then(r => r.data),
  get: (id: string) => api.get<CustomerDto>(`/api/v1/customers/${id}`).then(r => r.data),
  create: (payload: Partial<CustomerDto>) =>
    api.post<{ id: string }>("/api/v1/customers", payload).then(r => r.data),
  update: (id: string, payload: Partial<CustomerDto>) =>
    api.put(`/api/v1/customers/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/customers/${id}`),
};

// ---------- Stock ----------
export interface StockAvailabilityDto {
  itemId: string; itemName: string; sku: string;
  batchId: string; batchNo: string; expiryDate?: string;
  warehouseId: string; warehouseName: string;
  onHandQty: number; reservedQty: number; availableQty: number;
  costPrice: number; mrp: number;
  /** Primary bin for this item in this warehouse — where to walk to pick it. */
  binCode?: string | null;
  binName?: string | null;
}

export const stockApi = {
  availability: (params: { itemId?: string; warehouseId?: string } = {}) =>
    api.get<StockAvailabilityDto[]>("/api/v1/stock/availability", { params }).then(r => r.data),
};

// ---------- Purchase Orders ----------
export interface PurchaseOrderListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  vendorId: string; vendorName: string; warehouseId: string;
  expectedDeliveryDate?: string; totalAmount: number; lineCount: number;
}

export interface CreatePoLineRequest {
  itemId: string; qty: number; rate: number; taxAmount: number;
}

export interface CreatePurchaseOrderRequest {
  vendorId: string;
  warehouseId: string;
  documentDate: string;
  expectedDeliveryDate?: string;
  vendorRef?: string;
  notes?: string;
  lines: CreatePoLineRequest[];
}

export const purchaseOrdersApi = {
  list: (params: { status?: string; vendorId?: string; fromDate?: string; toDate?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PurchaseOrderListDto>>("/api/v1/purchase-orders", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<PurchaseOrderDetailDto>(`/api/v1/purchase-orders/${id}`).then(r => r.data),
  create: (payload: CreatePurchaseOrderRequest) =>
    api.post<{ id: string }>("/api/v1/purchase-orders", payload).then(r => r.data),
};

// ---------- GRN ----------
export interface CreateGrnLineRequest {
  purchaseOrderLineId: string; itemId: string;
  batchNo: string; expiryDate?: string;
  receivedQty: number; acceptedQty: number; rejectedQty: number;
  unitCost: number; remarks?: string;
  /** MRP printed on the pack. Omitted leaves the batch unpriced and billing on the item default. */
  mrp?: number;
}

export interface CreateGrnRequest {
  purchaseOrderId: string; warehouseId: string;
  documentDate: string; deliveryNoteRef?: string; notes?: string;
  lines: CreateGrnLineRequest[];
}

export const grnApi = {
  list: (params: { status?: string; vendorId?: string; purchaseOrderId?: string; page?: number; pageSize?: number } = {}) =>
    api.get(`/api/v1/grns`, { params }).then(r => r.data),
  get: (id: string) => api.get(`/api/v1/grns/${id}`).then(r => r.data),
  create: (payload: CreateGrnRequest) =>
    api.post<{ id: string }>("/api/v1/grns", payload).then(r => r.data),
  post: (id: string) => api.post(`/api/v1/grns/${id}/post`),
};

// ---------- Accounting + Reports + Audit (S5) ----------
export interface AccountDto {
  id: string; accountGroupId?: string; accountGroupName?: string;
  code: string; name: string; accountType: string;
  isActive: boolean; displayOrder?: number;
}
export type AccountTypeName = "Asset" | "Liability" | "Equity" | "Income" | "Expense";
export interface AccountGroupNode {
  id: string; parentId?: string | null; code: string; name: string; accountCount: number;
}
export interface AccountBalanceRow {
  id: string; code: string; name: string; accountType: AccountTypeName;
  isActive: boolean; debit: number; credit: number;
}
export const accountsApi = {
  list: (params: { search?: string; type?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<AccountDto>>("/api/v1/accounts", { params }).then(r => r.data),
  // Chart-of-accounts tree + per-group accounts with posted balances
  groupTree: () => api.get<AccountGroupNode[]>("/api/v1/accounts/groups/tree").then(r => r.data),
  byGroup: (groupId: string, asOf?: string) =>
    api.get<AccountBalanceRow[]>(`/api/v1/accounts/by-group/${groupId}`, { params: asOf ? { asOf } : {} }).then(r => r.data),
  // Group CRUD
  createGroup: (b: { parentId?: string | null; code: string; name: string }) =>
    api.post<null>("/api/v1/accounts/groups", b).then(r => r.data),
  updateGroup: (id: string, name: string) =>
    api.put(`/api/v1/accounts/groups/${id}`, { id, name }),
  deleteGroup: (id: string) => api.delete(`/api/v1/accounts/groups/${id}`),
  // Account CRUD
  createAccount: (b: { accountGroupId: string; code: string; name: string; accountType: AccountTypeName; displayOrder?: number | null }) =>
    api.post<null>("/api/v1/accounts", b).then(r => r.data),
  updateAccount: (id: string, b: { name: string; isActive: boolean; displayOrder?: number | null }) =>
    api.put(`/api/v1/accounts/${id}`, { id, ...b }),
  deleteAccount: (id: string) => api.delete(`/api/v1/accounts/${id}`),
};

export interface JournalLineInput {
  accountId: string; debit: number; credit: number; description?: string;
}
export interface CreateJournalEntryRequest {
  entryDate: string; description?: string;
  referenceType?: string; referenceId?: string;
  lines: JournalLineInput[];
}
export const journalsWriteApi = {
  create: (payload: CreateJournalEntryRequest) =>
    api.post<{ id: string }>("/api/v1/journal-entries", payload).then(r => r.data),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/journal-entries/${id}/post`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

// ---------- Journals (read) ----------
export interface JournalLineReadDto {
  id: string; accountId: string; accountCode: string; accountName: string;
  debit: number; credit: number; description?: string;
}
export interface JournalEntryListDto {
  id: string; entryNo: string; entryDate: string; status: string;
  referenceType?: string; referenceId?: string; description?: string;
  totalDebit: number; totalCredit: number; isAutoGenerated: boolean;
}
export interface JournalEntryDetailDto extends JournalEntryListDto {
  rowVersion: string;
  lines: JournalLineReadDto[];
}
export const journalsApi = {
  list: (params: {
    status?: string; referenceType?: string;
    fromDate?: string; toDate?: string;
    page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<JournalEntryListDto>>("/api/v1/journal-entries", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<JournalEntryDetailDto>(`/api/v1/journal-entries/${id}`).then(r => r.data),
};

export interface PeriodDto {
  id: string; financialYearId: string; name: string;
  startDate: string; endDate: string; isClosed: boolean;
  rowVersion: string;
}
export interface CreatePeriodRequest {
  financialYearId: string; name: string;
  startDate: string; endDate: string;
}
// ---------- Roles write (S6.1) ----------
export interface PermissionDto {
  id: string; code: string; module: string; resource: string; action: string;
  description?: string;
}
export interface RoleWithPermissionsDto {
  id: string; name: string; code: string; isSystem: boolean;
  permissions: PermissionDto[];
}
export const permissionsApi = {
  list: (module?: string) =>
    api.get<PermissionDto[]>("/api/v1/permissions", { params: { module } }).then(r => r.data),
};
export const rolesApi = {
  list: () => api.get<{ id: string; name: string; code: string; isSystem: boolean; permissionCount: number }[]>("/api/v1/roles").then(r => r.data),
  get: (id: string) => api.get<RoleWithPermissionsDto>(`/api/v1/roles/${id}`).then(r => r.data),
  create: (payload: { name: string; code: string }) =>
    api.post<{ id: string }>("/api/v1/roles", payload).then(r => r.data),
  assignPermissions: (id: string, permissionCodes: string[]) =>
    api.put(`/api/v1/roles/${id}/permissions`, { permissionCodes }),
};

// ---------- Branches write (S6.2) ----------
export interface BranchDetailDto {
  id: string; code: string; name: string;
  gstin?: string; addressJson?: string;
  isActive: boolean; rowVersion: string;
}
export const branchesWriteApi = {
  get: (id: string) => api.get<BranchDetailDto>(`/api/v1/branches/${id}`).then(r => r.data),
  create: (payload: { code: string; name: string; gstin?: string; addressJson?: string }) =>
    api.post<{ id: string }>("/api/v1/branches", payload).then(r => r.data),
  update: (id: string, payload: { id: string; code: string; name: string; gstin?: string; addressJson?: string; isActive: boolean }) =>
    api.put(`/api/v1/branches/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/branches/${id}`),
};

// ---------- Warehouses (S6.3) ----------
export type WarehouseType = "Main" | "Satellite" | "Cold" | "Quarantine" | "Consignment";
export interface WarehouseDto {
  id: string; code: string; name: string;
  warehouseType: WarehouseType;
  branchId: string; branchName: string;
  managerUserId?: string; isActive: boolean;
}
export interface WarehouseDetailDto extends WarehouseDto { rowVersion: string }
export const warehousesApi = {
  list: (params: { branchId?: string; type?: WarehouseType; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<WarehouseDto>>("/api/v1/warehouses", { params }).then(r => r.data),
  get: (id: string) => api.get<WarehouseDetailDto>(`/api/v1/warehouses/${id}`).then(r => r.data),
  create: (payload: { code: string; name: string; warehouseType: WarehouseType; branchId: string; managerUserId?: string }) =>
    api.post<{ id: string }>("/api/v1/warehouses", payload).then(r => r.data),
  update: (id: string, payload: { id: string; code: string; name: string; warehouseType: WarehouseType; managerUserId?: string; isActive: boolean }) =>
    api.put(`/api/v1/warehouses/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/warehouses/${id}`),
};

// ---------- Warehouse bins/locations + item-bin assignments (Feedback Code 3) ----------
export type LocationLevel = "Zone" | "Aisle" | "Rack" | "Bin";
export interface WarehouseLocationDto {
  id: string; warehouseId: string; parentId?: string | null;
  code: string; name: string; level: LocationLevel;
}
export interface WarehouseLocationPayload {
  warehouseId?: string; parentId?: string | null;
  code: string; name: string; level: LocationLevel;
}
export const warehouseLocationsApi = {
  list: (params: { warehouseId: string; search?: string; page?: number; pageSize?: number }) =>
    api.get<PaginatedList<WarehouseLocationDto>>("/api/v1/warehouse-locations", { params }).then(r => r.data),
  create: (payload: WarehouseLocationPayload) =>
    api.post<{ id: string }>("/api/v1/warehouse-locations", payload).then(r => r.data),
  update: (id: string, payload: WarehouseLocationPayload) =>
    api.put(`/api/v1/warehouse-locations/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/warehouse-locations/${id}`),
};

export interface ItemBinDto {
  id: string; itemId: string; itemSku: string; itemName: string;
  warehouseId: string; warehouseLocationId: string;
  locationCode: string; locationName: string; locationLevel: LocationLevel; isPrimary: boolean;
}
export const itemBinsApi = {
  list: (params: { warehouseId: string; itemId?: string; page?: number; pageSize?: number }) =>
    api.get<PaginatedList<ItemBinDto>>("/api/v1/item-bins", { params }).then(r => r.data),
  create: (payload: { itemId: string; warehouseId: string; warehouseLocationId: string; isPrimary: boolean }) =>
    api.post<{ id: string }>("/api/v1/item-bins", payload).then(r => r.data),
  update: (id: string, isPrimary: boolean) =>
    api.put(`/api/v1/item-bins/${id}`, { id, isPrimary }),
  delete: (id: string) => api.delete(`/api/v1/item-bins/${id}`),
};

// ---------- Feature flags ----------
export type FeaturePhaseName = "Mvp1" | "Mvp2" | "Final";
export interface FeatureFlagDto {
  featureKey: string;
  phase: FeaturePhaseName;
  description: string;
  defaultEnabled: boolean;
  isEnabled: boolean;
}
export const featureFlagsApi = {
  list: () => api.get<FeatureFlagDto[]>("/api/v1/feature-flags").then(r => r.data),
  set: (featureKey: string, enabled: boolean) =>
    api.put(`/api/v1/feature-flags/${encodeURIComponent(featureKey)}`, { enabled }),
};

// ---------- Tenant settings (Phase 5 — read-only) ----------
/**
 * Where service revenue reads its account head from. Chosen per client at onboarding: most map
 * the head on each service item, some maintain the chart of accounts one level up on the group.
 */
export type ServiceAccountMappingMode = "ServiceItem" | "ServiceGroup";

export interface TenantSettingsDto {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  taxRegime: "None" | "Gst" | "Vat";
  serviceAccountMappingMode: ServiceAccountMappingMode;
  createdAt: string;
  /** Discount ceiling for anyone who can raise a bill, as a percent of the gross. */
  standardDiscountLimitPct: number;
  /** Ceiling for a holder of `sales.discount.override`. */
  elevatedDiscountLimitPct: number;
  /** How many days after a bill a return is still accepted. */
  returnWindowDays: number;
  /** Whether Schedule H/H1/X drugs may be taken back into sellable stock. */
  allowScheduleDrugReturns: boolean;
  /** W24 — how bills are rounded before they are asked for. */
  invoiceRoundingMode: InvoiceRoundingMode;
  /** House palette a user gets before choosing one, and on every new device. */
  defaultThemeId: string;
  /** When false, the tenant default is the only palette and the picker is read-only. */
  allowUserThemeOverride: boolean;
}

/** How a bill is rounded to the rupee (W24). */
export type InvoiceRoundingMode = "None" | "Nearest" | "Up" | "Down";

export const tenantsApi = {
  current: () => api.get<TenantSettingsDto>("/api/v1/tenants/current").then(r => r.data),
  /** Unlike the tax regime this is changeable after go-live — posted entries keep their accounts. */
  setServiceAccountMapping: (mode: ServiceAccountMappingMode) =>
    api.put("/api/v1/tenants/current/service-account-mapping", { mode }),
  setDiscountLimits: (standardDiscountLimitPct: number, elevatedDiscountLimitPct: number) =>
    api.put("/api/v1/tenants/current/discount-limits",
      { standardDiscountLimitPct, elevatedDiscountLimitPct }),
  /**
   * Tenant appearance. Governs the theme only — light/dark and density stay per-user, because
   * they are legibility settings and a night-shift terminal differs from a daylit reception desk.
   */
  setTheme: (defaultThemeId: string, allowUserThemeOverride: boolean) =>
    api.put("/api/v1/tenants/current/theme", { defaultThemeId, allowUserThemeOverride }),
  setReturnPolicy: (returnWindowDays: number, allowScheduleDrugReturns: boolean) =>
    api.put("/api/v1/tenants/current/return-policy",
      { returnWindowDays, allowScheduleDrugReturns }),
  /** W24 — applies from the next bill; the difference posts to 5200 Rounding Off. */
  setInvoiceRounding: (mode: InvoiceRoundingMode) =>
    api.put("/api/v1/tenants/current/invoice-rounding", { mode }),
};

// ---------- Quotations (UC-M1-PROC-07) ----------
export type DocumentStatusName = "Draft" | "Submitted" | "Approved" | "Posted" | "Cancelled";
export interface QuotationLineReadDto {
  id: string; itemId: string; itemSku: string; itemName: string;
  qty: number; requestedRate: number; quotedRate?: number; taxAmount: number;
  effectiveRate: number; lineTotal: number;
}
export interface QuotationListDto {
  id: string; documentNo: string; documentDate: string; status: DocumentStatusName;
  vendorId: string; vendorName: string; validUntilDate?: string;
  totalAmount: number; convertedToPurchaseOrderId?: string;
}
export interface QuotationDetailDto extends Omit<QuotationListDto, "vendorName"> {
  vendorName: string; warehouseId: string; vendorRef?: string; notes?: string;
  lines: QuotationLineReadDto[]; rowVersion: string;
}
export interface QuotationLineInput {
  itemId: string; qty: number; requestedRate: number; taxAmount: number;
}
export const quotationsApi = {
  list: (params: { status?: DocumentStatusName; vendorId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<QuotationListDto>>("/api/v1/quotations", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<QuotationDetailDto>(`/api/v1/quotations/${id}`).then(r => r.data),
  create: (payload: {
    vendorId: string; warehouseId: string; validUntilDate?: string;
    vendorRef?: string; notes?: string; lines: QuotationLineInput[];
  }) => api.post<{ id: string }>("/api/v1/quotations", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/quotations/${id}/submit`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  approve: (id: string, rowVersion: string,
            lines: { lineId: string; quotedRate: number }[]) =>
    api.post(`/api/v1/quotations/${id}/approve`, { id, lines }, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  cancel: (id: string, rowVersion: string) =>
    api.post(`/api/v1/quotations/${id}/cancel`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  convertToPo: (id: string, rowVersion: string) =>
    api.post<{ purchaseOrderId: string }>(`/api/v1/quotations/${id}/convert-to-po`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }).then(r => r.data),
  delete: (id: string) => api.delete(`/api/v1/quotations/${id}`),
};

// ---------- Billing offers (UC-M1-SAL-05) ----------
export type DiscountType = "Percent" | "Amount";
export interface BillingOfferDto {
  id: string; name: string; description?: string;
  itemId?: string; itemName?: string;
  discountType: DiscountType; discountValue: number;
  validFrom: string; validTo: string; isActive: boolean;
}
export const billingOffersApi = {
  active: (itemId?: string) =>
    api.get<BillingOfferDto[]>("/api/v1/billing-offers/active", {
      params: itemId ? { itemId } : {},
    }).then(r => r.data),
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<BillingOfferDto>>("/api/v1/billing-offers", { params }).then(r => r.data),
  create: (payload: {
    name: string; description?: string; itemId?: string;
    discountType: DiscountType; discountValue: number;
    validFrom: string; validTo: string;
  }) => api.post<{ id: string }>("/api/v1/billing-offers", payload).then(r => r.data),
  update: (id: string, payload: {
    id: string; name: string; description?: string; itemId?: string;
    discountType: DiscountType; discountValue: number;
    validFrom: string; validTo: string; isActive: boolean;
  }) => api.put(`/api/v1/billing-offers/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/billing-offers/${id}`),
};

export const periodsApi = {
  list: (params: { page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PeriodDto>>("/api/v1/financial-periods", { params }).then(r => r.data),
  create: (payload: CreatePeriodRequest) =>
    api.post<{ id: string }>("/api/v1/financial-periods", payload).then(r => r.data),
  close: (id: string, rowVersion: string) =>
    api.post(`/api/v1/financial-periods/${id}/close`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  reopen: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/financial-periods/${id}/reopen`, { reason }, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

export interface TrialBalanceRow {
  accountId: string; code: string; name: string; accountType: string;
  totalDebit: number; totalCredit: number; net: number;
}
export interface TrialBalanceReport {
  asOf: string; totalDebit: number; totalCredit: number; isBalanced: boolean;
  rows: TrialBalanceRow[];
}
export interface StockOnHandRow {
  itemId: string; sku: string; itemName: string; form?: string; strength?: string;
  batchId: string; batchNo?: string; expiryDate?: string;
  warehouseId: string; warehouseName: string;
  onHand: number; reserved: number; available: number;
}
export interface PoStatusRow {
  vendorId: string; vendorName: string;
  draft: number; submitted: number; approved: number; posted: number; cancelled: number;
  totalValue: number;
}
export interface DailySalesRow {
  day: string; billCount: number; subTotal: number; tax: number; netAmount: number;
}
export const reportsApi = {
  trialBalance: (asOf?: string) =>
    api.get<TrialBalanceReport>("/api/v1/reports/trial-balance", { params: { asOf } }).then(r => r.data),
  stockOnHand: (params: { warehouseId?: string; itemId?: string } = {}) =>
    api.get<StockOnHandRow[]>("/api/v1/reports/stock-on-hand", { params }).then(r => r.data),
  poStatus: (params: { fromDate?: string; toDate?: string } = {}) =>
    api.get<PoStatusRow[]>("/api/v1/reports/po-status", { params }).then(r => r.data),
  dailySales: (fromDate: string, toDate: string) =>
    api.get<DailySalesRow[]>("/api/v1/reports/daily-sales", { params: { fromDate, toDate } }).then(r => r.data),
};

export interface AuditLogDto {
  id: string; tenantId: string; branchId?: string; userId?: string;
  action: string; entityType?: string; entityId?: string;
  beforeJson?: unknown; afterJson?: unknown;
  ipAddress?: string; correlationId?: string;
  occurredAt: string;
}
export const auditApi = {
  list: (params: {
    userId?: string; action?: string; entityType?: string; entityId?: string;
    fromDate?: string; toDate?: string; page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<AuditLogDto>>("/api/v1/audit-logs", { params }).then(r => r.data),
};

// ---------- Pricing + Requisitions + HealNTrack + Reports (S9) ----------
export type PriceListType = "OP" | "IP" | "Corporate" | "Insurance" | "CustomerSpecific";

export interface PriceListDto {
  id: string; code: string; name: string; listType: PriceListType;
  customerId?: string; isActive: boolean; priority: number; lineCount: number;
}
export interface PriceListLineDto {
  id: string; itemId: string; itemSku: string; itemName: string;
  rate: number; effectiveFrom: string; effectiveTo?: string;
  /** Rate card this line belongs to (W11), with its live state. */
  tariffId?: string | null; tariffCode?: string | null; tariffStatus?: TariffStatus | null;
  /** How the line arrives at its rate (W13). A derived line's `rate` is 0 and means nothing. */
  derivation: RateDerivation;
  derivedFromTariffId?: string | null; derivedFromTariffCode?: string | null;
  /** Signed — −15 is fifteen percent under the basis. */
  derivationAdjustmentPct: number;
}
export interface PriceListDetailDto extends PriceListDto {
  rowVersion: string; lines: PriceListLineDto[];
}
export interface UpsertPriceListLineRequest {
  id?: string; itemId: string; rate: number;
  effectiveFrom: string; effectiveTo?: string;
  /** Rate card to attach the line to (W11). Omit to keep it standalone. */
  tariffId?: string | null;
  /**
   * How the line is priced (W13). Omit for a typed amount. `Mrp` charges the item's printed MRP
   * moved by the adjustment; `BaseTariff` charges the same item's rate on `derivedFromTariffId`.
   */
  derivation?: RateDerivation;
  derivedFromTariffId?: string | null;
  derivationAdjustmentPct?: number;
}
// ---------- Packages and bundles (W18) ----------

export type PackageType = "HealthCheck" | "Maternity" | "Surgical" | "DayCare" | "Other";
/** What happens when less of a component is delivered than the package includes. */
export type SkippedComponentPolicy = "NoRefund" | "ProRataCredit";
/** What happens when more is delivered than it includes. */
export type ExtraComponentPolicy = "ChargeSeparately" | "IncludedNoCharge";

export interface PackageComponentDto {
  id: string;
  /** Service, Item or Rx — this becomes the invoice line's own type. */
  lineType: "Service" | "Item" | "Rx";
  serviceId?: string | null; serviceName?: string | null;
  itemId?: string | null; itemName?: string | null; sku?: string | null;
  qty: number;
  sortOrder: number;
  /** What this component costs on its own today. Null when nothing prices it. */
  listRate?: number | null;
}

export interface PackageDto {
  id: string; code: string; name: string; description?: string | null;
  packageType: PackageType;
  price: number; currencyId?: string | null; currencyCode?: string | null;
  insuranceSchemeId?: string | null; insuranceSchemeName?: string | null;
  effectiveFrom: string; effectiveTo?: string | null; isActive: boolean;
  skippedPolicy: SkippedComponentPolicy;
  extraPolicy: ExtraComponentPolicy;
  /** How often it may be billed in one episode — an admission for IP, one bill for OP. */
  maxPerEpisode: number;
  componentCount: number;
  rowVersion: string;
  components: PackageComponentDto[];
}

export interface UpsertPackageComponentRequest {
  lineType: "Service" | "Item" | "Rx";
  serviceId?: string | null;
  itemId?: string | null;
  qty: number;
  sortOrder?: number;
}

export interface UpsertPackageRequest {
  code: string; name: string; description?: string | null;
  packageType: PackageType;
  price: number; currencyId?: string | null; insuranceSchemeId?: string | null;
  effectiveFrom: string; effectiveTo?: string | null;
  skippedPolicy: SkippedComponentPolicy;
  extraPolicy: ExtraComponentPolicy;
  maxPerEpisode: number;
  components: UpsertPackageComponentRequest[];
  isActive?: boolean;
}

export const packagesApi = {
  list: (params: {
    packageType?: PackageType; isActive?: boolean; insuranceSchemeId?: string;
    /** Narrows to what can actually be sold on that date — what the billing screen asks for. */
    availableOn?: string;
    search?: string; page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<PackageDto>>("/api/v1/packages", { params }).then(r => r.data),
  get: (id: string) => api.get<PackageDto>(`/api/v1/packages/${id}`).then(r => r.data),
  create: (payload: UpsertPackageRequest) =>
    api.post<{ id: string }>("/api/v1/packages", payload).then(r => r.data),
  update: (id: string, payload: UpsertPackageRequest) =>
    api.put(`/api/v1/packages/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/packages/${id}`),
};

/** A package to put on a bill, and what of it was actually delivered. */
export interface PackageLineRequest {
  packageId: string;
  /** Per-component overrides. Omit to bill the package exactly as defined. */
  components?: {
    componentId: string;
    /** Below the included quantity is a skip; above it is an extra. */
    qty: number;
    batchId?: string | null;
    prescriptionRef?: string | null;
  }[];
}

export interface QuotedPackageLineDto {
  description: string; qty: number; rate: number; amount: number;
  taxAmount: number; isExtra: boolean;
}

export interface QuotedPackageDto {
  packageId: string; code: string; name: string;
  /** What is charged, after any credit for components not delivered. */
  price: number;
  listPrice: number;
  /** What the same basket would have cost billed component by component. */
  componentValue: number;
  /** componentValue − price. The saving being offered. */
  saving: number;
  lines: QuotedPackageLineDto[];
  notes: string[];
}

// ---------- Why does this cost this (W17) ----------

/** One price-list line that was in the running for an item — or was not, and why. */
export interface ItemPriceCandidate {
  priceListId: string; listCode: string; listName: string; listType: PriceListType;
  listPriority: number; customerId?: string | null;
  tariffId?: string | null; tariffCode?: string | null;
  tariffStatus?: TariffStatus | null; tariffPriority?: number | null;
  effectiveFrom: string; effectiveTo?: string | null;
  derivation: RateDerivation; derivedFromTariffCode?: string | null;
  derivationAdjustmentPct: number;
  /** What this line would charge, with any derivation worked out. */
  rate?: number | null;
  /** Where it sits in the precedence chain. Null when it never got that far. */
  tier?: number | null;
  eligible: boolean;
  /** Why it did not count. Null when it did. */
  ineligibleReason?: string | null;
  won: boolean;
}

export interface ItemPriceExplanation {
  itemId: string; sku: string; itemName: string;
  on: string; context: PriceListType; customerId?: string | null;
  /** False makes the whole candidate list moot — the engine does not run for this tenant. */
  priceListsEnabled: boolean;
  batchId?: string | null; batchNo?: string | null;
  batchMrp?: number | null; itemDefaultMrp?: number | null; mrpCeiling?: number | null;
  candidates: ItemPriceCandidate[];
  rate?: number | null;
  source: "PriceList" | "BatchMrp" | "ItemDefaultMrp" | "None";
  sourceId?: string | null;
  explanation: string;
  /** Set when billing this item right now would fail, and why. */
  problem?: string | null;
}

export const priceExplainApi = {
  /** Not gated on the price-list feature flag — "the engine is off" is an answer worth giving. */
  item: (params: {
    itemId: string; customerId?: string; context?: PriceListType;
    onDate?: string; batchId?: string;
  }) => api.get<ItemPriceExplanation>("/api/v1/pricing/explain/item", { params }).then(r => r.data),
};

export const priceListsApi = {
  list: (params: { type?: PriceListType; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PriceListDto>>("/api/v1/price-lists", { params }).then(r => r.data),
  get: (id: string) => api.get<PriceListDetailDto>(`/api/v1/price-lists/${id}`).then(r => r.data),
  create: (payload: { code: string; name: string; listType: PriceListType; customerId?: string; priority: number; lines: UpsertPriceListLineRequest[] }) =>
    api.post<{ id: string }>("/api/v1/price-lists", payload).then(r => r.data),
  updateLines: (id: string, lines: UpsertPriceListLineRequest[]) =>
    api.put(`/api/v1/price-lists/${id}/lines`, { lines }),
  delete: (id: string) => api.delete(`/api/v1/price-lists/${id}`),
};

// ---------- Tariffs / rate cards (W11) ----------

/**
 * Only an Active card inside its window prices anything. The path is
 * Draft → Submitted → Approved → Active (W16); Submitted and Approved are frozen — nothing can be
 * added to or edited on a card while it is being reviewed.
 */
export type TariffStatus = "Draft" | "Submitted" | "Approved" | "Active" | "Superseded";

export interface TariffDto {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  insuranceSchemeId?: string | null;
  insuranceSchemeName?: string | null;
  currencyId?: string | null;
  currencyCode?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  priority: number;
  status: TariffStatus;
  /** How many price rows belong to this card. Activating an empty one is refused. */
  serviceChargeCount: number;
  priceListLineCount: number;
  rowVersion: string;
  /** Which revision of the card this is (W15) — a card and its successor share a code. */
  version: number;
  /** The version this one retires when it is activated. */
  supersedesTariffId?: string | null;
  /** Who sent it for approval (W16). Cleared when a card comes back rejected or withdrawn. */
  submittedBy?: string | null;
  submittedByName?: string | null;
  submittedAt?: string | null;
  /** Who signed it off — the record of who took responsibility for what patients are charged. */
  approvedBy?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  /** Why a checker sent it back. Kept on the draft so the maker knows what to fix. */
  rejectionReason?: string | null;
}

/** How a price row arrives at its rate (W13). */
export type RateDerivation = "Absolute" | "BaseTariff" | "Mrp";

/** One row of a payer's spreadsheet. Give exactly one of serviceCode or sku. */
export interface TariffImportRowRequest {
  serviceCode?: string | null;
  sku?: string | null;
  rate: number;
}

export interface TariffImportResultRow {
  rowNumber: number;
  key: string;
  rate: number;
  /** Added / Updated / Unchanged / Error — what committing would do to this row. */
  outcome: "Added" | "Updated" | "Unchanged" | "Error";
  previousRate?: number | null;
  problem?: string | null;
}

export interface TariffImportResultDto {
  tariffId: string;
  tariffCode: string;
  committed: boolean;
  added: number;
  updated: number;
  unchanged: number;
  errors: number;
  rows: TariffImportResultRow[];
}

export interface UpsertTariffRequest {
  code: string;
  name: string;
  description?: string | null;
  insuranceSchemeId?: string | null;
  currencyId?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  priority: number;
}


// W25 — a rate card restated in the reporting currency.
export interface ConvertedChargeRow {
  serviceChargeId: string;
  serviceItemName: string;
  rate: number;
  currencyCode?: string | null;
  /** Null when the row could not be converted — see `problem`. */
  rateInBase?: number | null;
  /** Null when the row was already in base, so no rate was applied. */
  exchangeRateUsed?: number | null;
  /** Why it could not convert. Null on success and on a row already in base. */
  problem?: string | null;
}

export interface TariffInBaseCurrencyDto {
  tariffId: string;
  code: string;
  name: string;
  on: string;
  baseCurrencyCode?: string | null;
  rowCount: number;
  /** Rows left out of the total below, each one for a stated reason. */
  unconvertedCount: number;
  totalInBase: number;
  rows: ConvertedChargeRow[];
}
export const tariffsApi = {
  list: (params: { status?: TariffStatus; insuranceSchemeId?: string; search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<TariffDto>>("/api/v1/tariffs", { params }).then(r => r.data),
  get: (id: string) => api.get<TariffDto>(`/api/v1/tariffs/${id}`).then(r => r.data),
  create: (payload: UpsertTariffRequest) =>
    api.post<{ id: string }>("/api/v1/tariffs", payload).then(r => r.data),
  update: (id: string, payload: UpsertTariffRequest) =>
    api.put(`/api/v1/tariffs/${id}`, { id, ...payload }),
  /**
   * Separately permissioned — this is what makes a card start changing what patients pay. Since
   * W16 it accepts only Active (from an approved card) and Superseded; the review states are
   * reached through submit/approve/reject, which carry the maker/checker rules.
   */
  setStatus: (id: string, status: TariffStatus) =>
    api.put(`/api/v1/tariffs/${id}/status`, { status }),
  /** W16 — send a draft for approval. Freezes the card while it waits. */
  submit: (id: string) => api.post(`/api/v1/tariffs/${id}/submit`),
  /** W16 — sign a submitted card off. Refused to the person who submitted it. */
  approve: (id: string) => api.post(`/api/v1/tariffs/${id}/approve`),
  /** W16 — send it back to the maker with a reason, which is kept on the draft. */
  reject: (id: string, reason: string) => api.post(`/api/v1/tariffs/${id}/reject`, { reason }),
  /** W16 — the submitter pulls their own card back out of review. Not a decision. */
  withdraw: (id: string) => api.post(`/api/v1/tariffs/${id}/withdraw`),
  /**
   * Clone this card's rows into a fresh draft, optionally moving every rate (W15). The clone
   * retires the original when it is activated, not when it is created.
   */
  nextVersion: (id: string, body: { effectiveFrom: string; effectiveTo?: string | null; adjustmentPct?: number }) =>
    api.post<{ id: string }>(`/api/v1/tariffs/${id}/next-version`, body).then(r => r.data),
  /**
   * Load rates into a draft card. Defaults to a dry run — the result reports row by row what
   * committing would do and which codes did not match.
   */
  import: (id: string, rows: TariffImportRowRequest[], dryRun = true) =>
    api.post<TariffImportResultDto>(`/api/v1/tariffs/${id}/import`, { rows, dryRun }).then(r => r.data),
  /**
   * W25 — this card's rows restated in the tenant's base currency, at the rate in force on the
   * given date (default today). Rows with no rate on file come back listed with the reason and
   * are counted out of the total rather than added in at their foreign face value.
   */
  inBaseCurrency: (id: string, on?: string) =>
    api.get<TariffInBaseCurrencyDto>(`/api/v1/tariffs/${id}/in-base-currency`,
      { params: on ? { on } : {} }).then(r => r.data),
  delete: (id: string) => api.delete(`/api/v1/tariffs/${id}`),
};

// The client no longer resolves item prices for itself. GET /api/v1/pricing/resolve still exists
// as a server-side and integration surface, but since W3 the billing screen asks
// salesInvoicesApi.quote instead — which runs the same pricer the invoice will, price list and
// MRP fallback included. Two client-side answers to "what does this cost" is the thing this whole
// change set removed.

export interface PurchaseRequisitionListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  requestingBranchId: string; neededByDate?: string;
  lineCount: number; convertedToPurchaseOrderId?: string;
}
export interface PurchaseRequisitionLineReadDto {
  id: string; itemId: string; itemName: string; sku: string;
  requestedQty: number; reason?: string;
}
export interface PurchaseRequisitionDetailDto extends PurchaseRequisitionListDto {
  departmentRef?: string; reason?: string; rowVersion: string;
  lines: PurchaseRequisitionLineReadDto[];
}
export const purchaseRequisitionsApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PurchaseRequisitionListDto>>("/api/v1/purchase-requisitions", { params }).then(r => r.data),
  get: (id: string) => api.get<PurchaseRequisitionDetailDto>(`/api/v1/purchase-requisitions/${id}`).then(r => r.data),
  create: (payload: { departmentRef?: string; neededByDate?: string; reason?: string; lines: { itemId: string; qty: number; reason?: string }[] }) =>
    api.post<{ id: string }>("/api/v1/purchase-requisitions", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/purchase-requisitions/${id}/submit`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  approve: (id: string, rowVersion: string) =>
    api.post(`/api/v1/purchase-requisitions/${id}/approve`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  reject: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/purchase-requisitions/${id}/reject`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  convertToPo: (id: string, payload: { vendorId: string; warehouseId: string; expectedDeliveryDate?: string; rateByLine: Record<string, number> }) =>
    api.post<{ purchaseOrderId: string }>(`/api/v1/purchase-requisitions/${id}/convert`, payload).then(r => r.data),
};

export interface HealNTrackPatientDto {
  uhid: string; fullName: string; gender: string; dob: string;
  phone?: string; branchCode?: string; admissionId?: string; wardCode?: string; isAdmitted: boolean;
}
export const healNTrackApi = {
  patient: (uhid: string) =>
    api.get<HealNTrackPatientDto>(`/api/v1/integrations/healntrack/v1/patients/${encodeURIComponent(uhid)}`).then(r => r.data),
};

// Patient identity by UHID (name/age/gender/UHID/phone), served from the stored Customer projection.
export interface PatientSummaryDto {
  uhid: string; name: string; gender?: string | null; dob?: string | null; phone?: string | null;
}
export const patientsApi = {
  summary: (uhid: string) =>
    api.get<PatientSummaryDto>(`/api/v1/customers/patient-summary/${encodeURIComponent(uhid)}`).then(r => r.data),
  /** Same block keyed by HealNTrack patientid — for queues whose rows carry no UHID yet. */
  summaryByPatientId: (patientId: number | string) =>
    api.get<PatientSummaryDto>(`/api/v1/customers/patient-summary/by-patient/${encodeURIComponent(String(patientId))}`).then(r => r.data),
};

export interface ConsolidatedStockRow {
  itemId: string; sku: string; itemName: string; form?: string; strength?: string;
  totalOnHand: number; totalReserved: number; totalAvailable: number; warehouseCount: number;
}
export interface AgingBucket { bucket0_30: number; bucket31_60: number; bucket61_90: number; bucketOver90: number }
export interface ArAgingRow { customerId: string; customerName: string; buckets: AgingBucket; total: number }
export interface ApAgingRow { vendorId: string; vendorName: string; buckets: AgingBucket; total: number }
export const reportsApiV2 = {
  consolidatedStock: () =>
    api.get<ConsolidatedStockRow[]>("/api/v1/reports/consolidated-stock").then(r => r.data),
  arAging: () => api.get<ArAgingRow[]>("/api/v1/reports/ar-aging").then(r => r.data),
  apAging: () => api.get<ApAgingRow[]>("/api/v1/reports/ap-aging").then(r => r.data),
};

// ---------- Shipments + Carriers + Vehicles + Drivers (S8) ----------
export type ShipmentStatus =
  | "Planned" | "Loaded" | "Dispatched" | "InTransit" | "Arrived" | "Delivered" | "Closed" | "Exception";

export interface ShipmentListDto {
  id: string; transferOrderId: string; transferOrderNo: string;
  carrierId?: string; carrierName?: string;
  vehicleId?: string; vehicleRegNo?: string;
  driverId?: string; driverName?: string;
  status: ShipmentStatus;
  requestedDeliveryDate?: string; actualDeliveryDate?: string;
  podStatus?: string;
  lineCount: number; milestoneCount: number;
}
export interface ShipmentMilestoneDto { id: string; status: ShipmentStatus; occurredAt: string; notes?: string }
export interface ProofOfDeliveryDto {
  id: string; receiverName: string;
  signatureUrl?: string; photoUrl?: string; notes?: string; capturedAt: string;
}
export interface ShipmentDetailDto extends ShipmentListDto {
  vehicleNo?: string; eWayBillNo?: string;
  rowVersion: string;
  lines: { id: string; transferLineId: string; qty: number }[];
  milestones: ShipmentMilestoneDto[];
  pod?: ProofOfDeliveryDto;
}
export const shipmentsApi = {
  list: (params: { status?: ShipmentStatus; transferOrderId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ShipmentListDto>>("/api/v1/shipments", { params }).then(r => r.data),
  get: (id: string) => api.get<ShipmentDetailDto>(`/api/v1/shipments/${id}`).then(r => r.data),
  create: (payload: { transferOrderId: string; carrierId?: string; vehicleId?: string; driverId?: string; eWayBillNo?: string; requestedDeliveryDate?: string }) =>
    api.post<{ id: string }>("/api/v1/shipments", payload).then(r => r.data),
  addMilestone: (id: string, status: ShipmentStatus, notes: string | undefined, rowVersion: string) =>
    api.post(`/api/v1/shipments/${id}/milestones`, { status, notes }, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  capturePod: (id: string, body: { receiverName: string; signatureUrl?: string; photoUrl?: string; notes?: string }, rowVersion: string) =>
    api.post(`/api/v1/shipments/${id}/pod`, body, { headers: { "If-Match": `"${rowVersion}"` } }),
};

export interface CarrierDto { id: string; code: string; name: string; phone?: string; isActive: boolean }
export const carriersApi = {
  list: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<CarrierDto>>("/api/v1/carriers", { params }).then(r => r.data),
  create: (payload: { code: string; name: string; phone?: string }) =>
    api.post<{ id: string }>("/api/v1/carriers", payload).then(r => r.data),
  update: (id: string, payload: { id: string; code: string; name: string; phone?: string; isActive: boolean }) =>
    api.put(`/api/v1/carriers/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/carriers/${id}`),
};

export interface VehicleDto {
  id: string; registrationNo: string;
  carrierId?: string; carrierName?: string;
  capacityKg: number; coldChainCapable: boolean;
  insuranceExpiry?: string; isActive: boolean;
}
export const vehiclesApi = {
  list: (params: { search?: string; carrierId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<VehicleDto>>("/api/v1/vehicles", { params }).then(r => r.data),
  create: (payload: { registrationNo: string; carrierId?: string; capacityKg: number; coldChainCapable: boolean; insuranceExpiry?: string }) =>
    api.post<{ id: string }>("/api/v1/vehicles", payload).then(r => r.data),
  update: (id: string, payload: { id: string; registrationNo: string; carrierId?: string; capacityKg: number; coldChainCapable: boolean; insuranceExpiry?: string; isActive: boolean }) =>
    api.put(`/api/v1/vehicles/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/vehicles/${id}`),
};

export interface DriverDto {
  id: string; name: string;
  licenseNo?: string; licenseExpiry?: string;
  mobile?: string; carrierId?: string; carrierName?: string; isActive: boolean;
}
export const driversApi = {
  list: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<DriverDto>>("/api/v1/drivers", { params }).then(r => r.data),
  create: (payload: { name: string; licenseNo?: string; licenseExpiry?: string; mobile?: string; carrierId?: string }) =>
    api.post<{ id: string }>("/api/v1/drivers", payload).then(r => r.data),
  update: (id: string, payload: { id: string; name: string; licenseNo?: string; licenseExpiry?: string; mobile?: string; carrierId?: string; isActive: boolean }) =>
    api.put(`/api/v1/drivers/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/drivers/${id}`),
};

// ---------- Transfer Orders (S7.1) ----------
export interface TransferOrderListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  sourceWarehouseId: string; sourceWarehouseName: string;
  destWarehouseId: string; destWarehouseName: string;
  lineCount: number;
  totalRequested: number; totalDispatched: number; totalReceived: number;
  inTransitQty: number;
  transferRequisitionId?: string;
}
export interface TransferOrderLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string; expiryDate?: string;
  requestedQty: number; dispatchedQty: number; receivedQty: number;
  inTransitQty: number; pendingDispatch: number; pendingReceipt: number;
  requisitionLineId?: string;
}
export interface TransferOrderDetailDto extends TransferOrderListDto {
  expectedDeliveryDate?: string; notes?: string;
  rowVersion: string; lines: TransferOrderLineDto[];
}
export interface CreateTransferOrderLineRequest {
  itemId: string; batchId: string; qty: number; requisitionLineId?: string;
}
export interface CreateTransferOrderRequest {
  sourceWarehouseId: string; destWarehouseId: string;
  expectedDeliveryDate?: string; notes?: string;
  transferRequisitionId?: string;
  lines: CreateTransferOrderLineRequest[];
}
export const transferOrdersApi = {
  list: (params: { status?: string; sourceWarehouseId?: string; destWarehouseId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<TransferOrderListDto>>("/api/v1/transfer-orders", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<TransferOrderDetailDto>(`/api/v1/transfer-orders/${id}`).then(r => r.data),
  create: (payload: CreateTransferOrderRequest) =>
    api.post<{ id: string }>("/api/v1/transfer-orders", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/submit`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  approve: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/approve`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  reject: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/reject`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  cancel: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/cancel`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  dispatch: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/dispatch`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  receive: (id: string, lines: { lineId: string; receivedQty: number }[], rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/receive`, { lines }, { headers: { "If-Match": `"${rowVersion}"` } }),
};

// ---------- Transfer Requisitions (S7.2) ----------
export interface TransferRequisitionListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  requestingBranchId: string; requestingWarehouseId: string; requestingWarehouseName: string;
  suggestedSourceWarehouseId?: string; suggestedSourceWarehouseName?: string;
  neededByDate?: string; lineCount: number;
  convertedToTransferOrderId?: string;
}
export interface TransferRequisitionLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  requestedQty: number; reason?: string;
}
export interface TransferRequisitionDetailDto extends TransferRequisitionListDto {
  reason?: string; rowVersion: string; lines: TransferRequisitionLineDto[];
}
export interface CreateTransferRequisitionLineRequest { itemId: string; qty: number; reason?: string }
export interface CreateTransferRequisitionRequest {
  requestingWarehouseId: string; suggestedSourceWarehouseId?: string;
  neededByDate?: string; reason?: string;
  lines: CreateTransferRequisitionLineRequest[];
}
export const transferRequisitionsApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<TransferRequisitionListDto>>("/api/v1/transfer-requisitions", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<TransferRequisitionDetailDto>(`/api/v1/transfer-requisitions/${id}`).then(r => r.data),
  create: (payload: CreateTransferRequisitionRequest) =>
    api.post<{ id: string }>("/api/v1/transfer-requisitions", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-requisitions/${id}/submit`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  approve: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-requisitions/${id}/approve`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  reject: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-requisitions/${id}/reject`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  convert: (id: string, sourceWarehouseId: string, batchByLine: Record<string, string>) =>
    api.post<{ transferOrderId: string }>(`/api/v1/transfer-requisitions/${id}/convert`,
      { sourceWarehouseId, batchByLine }).then(r => r.data),
};

// ---------- Purchase Invoices ----------
export type PurchaseInvoiceSource = "FromGrn" | "Direct";
export interface PurchaseInvoiceListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  source: PurchaseInvoiceSource;
  vendorId: string; vendorName: string;
  grnId?: string | null; grnNo?: string | null;
  vendorInvoiceNo?: string; vendorInvoiceDate?: string;
  subTotal: number; taxAmount: number; discountAmount: number; totalAmount: number;
}
export interface PurchaseInvoiceLineDto {
  id: string; grnLineId?: string | null; itemId: string; itemName: string; sku: string;
  qty: number; rate: number; taxAmount: number; discountAmount: number; lineTotal: number;
  batchNo?: string | null; expiryDate?: string | null; batchId?: string | null;
}
export interface PurchaseInvoiceDetailDto extends PurchaseInvoiceListDto {
  journalEntryId?: string; notes?: string; lines: PurchaseInvoiceLineDto[];
}
export interface CreatePurchaseInvoiceLineRequest {
  grnLineId: string; itemId: string; qty: number; rate: number;
  taxAmount: number; discountAmount: number;
}
export interface CreatePurchaseInvoiceRequest {
  grnId: string; documentDate: string;
  vendorInvoiceNo?: string; vendorInvoiceDate?: string;
  discountAmount: number; notes?: string;
  lines: CreatePurchaseInvoiceLineRequest[];
}
export interface CreateDirectPurchaseInvoiceLineRequest {
  itemId: string; batchNo: string; expiryDate?: string;
  qty: number; rate: number; taxAmount: number; discountAmount: number;
  /** MRP printed on the pack. Omitted leaves the batch unpriced and billing on the item default. */
  mrp?: number;
}

// ---------- Batches ----------
export interface BatchDto {
  id: string;
  itemId: string; itemSku: string; itemName: string;
  batchNo: string;
  expiryDate?: string | null;
  mrp: number;
  costPrice: number;
  isExpired: boolean;
  /** Total across every warehouse — enough to say whether a correction touches live stock. */
  onHandQty: number;
  rowVersion: string;
}

export const batchesApi = {
  list: (params: {
    itemId?: string; search?: string; unpricedOnly?: boolean;
    page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<BatchDto>>("/api/v1/batches", { params }).then(r => r.data),
  /** Corrects MRP and expiry. Cost price is deliberately not editable. */
  update: (id: string, payload: { mrp: number; expiryDate?: string | null; reason?: string }) =>
    api.put(`/api/v1/batches/${id}`, { id, ...payload }),
};
export interface CreateDirectPurchaseInvoiceRequest {
  vendorId: string; warehouseId: string; documentDate: string;
  vendorInvoiceNo?: string; vendorInvoiceDate?: string;
  discountAmount: number; notes?: string;
  lines: CreateDirectPurchaseInvoiceLineRequest[];
}
export const purchaseInvoicesApi = {
  list: (params: { status?: string; vendorId?: string; fromDate?: string; toDate?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PurchaseInvoiceListDto>>("/api/v1/purchase-invoices", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<PurchaseInvoiceDetailDto>(`/api/v1/purchase-invoices/${id}`).then(r => r.data),
  create: (payload: CreatePurchaseInvoiceRequest) =>
    api.post<{ id: string }>("/api/v1/purchase-invoices", payload).then(r => r.data),
  createDirect: (payload: CreateDirectPurchaseInvoiceRequest) =>
    api.post<{ id: string }>("/api/v1/purchase-invoices/direct", payload).then(r => r.data),
  post: (id: string) => api.post(`/api/v1/purchase-invoices/${id}/post`),
};

// ---------- Sales Invoices ----------
export interface SalesInvoiceListDto {
  id: string; documentNo: string; documentDate: string; status: string; billType: string;
  customerId?: string; customerName?: string; customerPhone?: string;
  netAmount: number; totalReceived: number; balanceDue: number; lineCount: number;
}

export type LineType = "Rx" | "Item" | "Service" | "Other";

export interface CreateSalesInvoiceLineRequest {
  lineType: LineType;
  itemId?: string;
  serviceId?: string;
  batchId?: string;
  description?: string;
  prescriptionRef?: string;
  substitutedFromItemId?: string;
  substituteReasonCode?: string;
  /**
   * Pharmacist accepted the substitution. Required by the API when the mapping is marked
   * RequiresPharmacistConfirm; the confirming user is taken from the session, not from here.
   */
  substituteConfirmed?: boolean;
  qty: number;
  rate: number;
  taxAmount: number;
  discountAmount: number;
  lineNo: number;
  /**
   * Whether the payer covers this line (W19). Ignored on a bill with no scheme. Defaults to
   * covered — the biller marks what the agreement excludes.
   */
  isPayerCovered?: boolean;
  /** Prescribing doctor, required by the API on a Schedule H1 or X line (W21). */
  prescriberName?: string | null;
  /** Their medical council registration number. */
  prescriberRegNo?: string | null;
  /**
   * Why this line is discounted. Required by the API once the discount passes the tenant's
   * standard ceiling — routine round-offs below it need no explanation.
   */
  discountReason?: string | null;
}

/** Patient identity carried onto the bill; `admissionId` is only meaningful for InPatient. */
export interface CreateSalesInvoiceRequest {
  uhid?: string;
  admissionId?: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  billType: "OutPatient" | "InPatient";
  warehouseId: string;
  documentDate: string;
  /**
   * Bill-level discount, on top of any line discounts. Checked against the caller's discount
   * ceiling server-side — a bill past it is refused with 422 DISCOUNT_LIMIT_EXCEEDED.
   */
  discountAmount: number;
  /** Why the bill carries a header-level discount. Same rule as the line reason. */
  discountReason?: string | null;
  notes?: string;
  lines: CreateSalesInvoiceLineRequest[];
  /**
   * Packages to bill (W18). Each expands server-side into ordinary lines with its fixed price
   * apportioned across them — the caller never states a package's price.
   */
  packages?: PackageLineRequest[];
  /** The attending consultant (W20). Every service line inherits it unless the line overrides. */
  consultantId?: string | null;
  /**
   * Billing dimensions the bill is priced under. Omit them and the server falls back to the
   * linked customer's standing values; send them to override for this bill only.
   */
  admissionTypeId?: string | null;
  personTypeId?: string | null;
  insuranceSchemeId?: string | null;
  insurancePolicyNo?: string | null;
  /**
   * Patient's out-of-pocket share. Omit it and the server takes the figure from the scheme's
   * contract (W12); send one to override for this bill.
   */
  coPayPct?: number | null;
}

// ---------- Sales returns / credit notes (W8) ----------

/** A line on the original bill, with how much of it can still come back. */
export interface ReturnableLineDto {
  salesInvoiceLineId: string;
  lineNo: number;
  lineType: LineType;
  itemId?: string | null;
  sku?: string | null;
  itemName?: string | null;
  batchId?: string | null;
  batchNo?: string | null;
  expiryDate?: string | null;
  qtySold: number;
  qtyReturned: number;
  qtyReturnable: number;
  rate: number;
  /** Why this line cannot be returned at all. Null means it can. */
  blockedReason?: string | null;
}

export interface SalesReturnListDto {
  id: string;
  documentNo: string;
  documentDate: string;
  status: DocumentStatusName;
  salesInvoiceId: string;
  salesInvoiceNo: string;
  customerName?: string | null;
  uhid?: string | null;
  reason: string;
  refundMode: PaymentLineRequest["mode"];
  netAmount: number;
  journalEntryId?: string | null;
}

export interface CreateSalesReturnRequest {
  salesInvoiceId: string;
  reason: string;
  lines: { salesInvoiceLineId: string; qty: number }[];
  refundMode: PaymentLineRequest["mode"];
  refundReference?: string | null;
}

export const salesReturnsApi = {
  list: (params: { status?: string; salesInvoiceId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<SalesReturnListDto>>("/api/v1/sales-returns", { params }).then(r => r.data),
  get: (id: string) => api.get(`/api/v1/sales-returns/${id}`).then(r => r.data),
  /** Quantity sold minus what has already come back, with a reason on anything blocked. */
  returnable: (salesInvoiceId: string) =>
    api.get<ReturnableLineDto[]>(`/api/v1/sales-returns/returnable/${salesInvoiceId}`).then(r => r.data),
  create: (payload: CreateSalesReturnRequest) =>
    api.post<{ id: string }>("/api/v1/sales-returns", payload).then(r => r.data),
  post: (id: string) => api.post(`/api/v1/sales-returns/${id}/post`),
};

/** Which of a bill's two payables a receipt answers (W19). */
export type PaidBy = "Patient" | "Payer";

export interface PaymentLineRequest {
  mode: "Cash" | "Card" | "Upi" | "BankTransfer" | "Cheque" | "Credit";
  amountReceived: number;
  reference?: string;
  /**
   * Defaults to the patient. A claim remittance is the deliberate exception, and marking it is
   * what makes the two balances tellable apart afterwards.
   */
  paidBy?: PaidBy;
}

/** One line to price. Mirrors the create-line shape, minus everything pricing doesn't read. */
export interface QuoteLineRequest {
  lineType: LineType;
  itemId?: string | null;
  serviceId?: string | null;
  batchId?: string | null;
  qty: number;
  discountAmount: number;
  /** Only read for free-text ("Other") lines, which have no catalog row behind them. */
  rate: number;
  /** Echoed back so the caller can match results to its own rows. */
  lineNo: number;
  /** Whether the payer covers this line (W19) — decides which side of the split it lands on. */
  isPayerCovered?: boolean;
  /** Who performed this service and earns a share of it (W20). Falls back to the attending. */
  consultantId?: string | null;
}

export interface QuoteSalesLinesRequest {
  customerId?: string | null;
  billType: "OutPatient" | "InPatient";
  lines: QuoteLineRequest[];
  admissionTypeId?: string | null;
  personTypeId?: string | null;
  insuranceSchemeId?: string | null;
  on?: string | null;
  headerDiscount?: number;
  /** Packages on the bill (W18), expanded by the same code the create path uses. */
  packages?: PackageLineRequest[];
}

/** Where a quoted rate came from — mirrors the server's PriceSource enum. */
export type PriceSource =
  | "ServiceCharge" | "ServiceRate" | "ServiceDefault"
  | "PriceList" | "BatchMrp" | "ItemDefaultMrp" | "FreeText"
  /** A share of a fixed package price — not a rate anybody set (W18). */
  | "Package";

export interface QuotedLineDto {
  lineNo: number;
  rate: number;
  taxAmount: number;
  source: PriceSource;
  sourceId?: string | null;
  /** W22 — the promotion the server applied to this line, if any. */
  offerId?: string | null;
  offerName?: string | null;
  offerDiscount: number;
}

export interface QuoteSalesLinesResultDto {
  lines: QuotedLineDto[];
  subTotal: number;
  taxAmount: number;
  discountAmount: number;
  netAmount: number;
  /**
   * Things worth showing the counter that do not stop the sale — currently an item dispensed
   * from batches printed at different MRPs. Refusals arrive as 422s from create, not here.
   */
  warnings: string[];
  /** Packages on this quote, expanded (W18). Empty on a bill with none. */
  packages: QuotedPackageDto[];
  /** W19 — how the bill divides. On an insured bill the counter collects patientPayable. */
  patientPayable: number;
  payerPayable: number;
  /** W24 — the rounding applied to reach netAmount. Signed. */
  roundOff: number;
}

export const salesInvoicesApi = {
  /**
   * What the server would charge for these lines. The billing screen calls this instead of
   * pricing locally, so the total quoted at the counter is the total the invoice is raised at.
   */
  quote: (payload: QuoteSalesLinesRequest) =>
    api.post<QuoteSalesLinesResultDto>("/api/v1/sales-invoices/quote", payload).then(r => r.data),
  list: (params: { status?: string; billType?: string; customerId?: string; fromDate?: string; toDate?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<SalesInvoiceListDto>>("/api/v1/sales-invoices", { params }).then(r => r.data),
  get: (id: string) =>
    api.get(`/api/v1/sales-invoices/${id}`).then(r => r.data),
  create: (payload: CreateSalesInvoiceRequest) =>
    api.post<{ id: string }>("/api/v1/sales-invoices", payload).then(r => r.data),
  /** `lasaAcknowledgedItemIds` carries the cashier's High-severity LASA confirmations. */
  submit: (id: string, rowVersion: string, lasaAcknowledgedItemIds?: string[]) =>
    api.post(`/api/v1/sales-invoices/${id}/submit`,
      lasaAcknowledgedItemIds?.length ? { lasaAcknowledgedItemIds } : null, {
        headers: { "If-Match": `"${rowVersion}"` },
      }),
  post: (id: string, payments: PaymentLineRequest[]) =>
    api.post(`/api/v1/sales-invoices/${id}/post`, { payments }),
  cancel: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/sales-invoices/${id}/cancel`, { reason }, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  /**
   * W17 — how one line came to cost what it did: the decision recorded when the bill was raised,
   * and separately what the same question answers today.
   */
  /**
   * W19 — record what a payer settled against a posted bill. Short payment is accepted and
   * leaves the balance outstanding; over-settlement is refused.
   */
  recordPayerSettlement: (id: string, body: {
    amount: number; mode: PaymentLineRequest["mode"]; reference?: string;
  }) => api.post<{ id: string }>(`/api/v1/sales-invoices/${id}/payer-settlement`, body)
    .then(r => r.data),
  explainLinePrice: (id: string, lineId: string) =>
    api.get<LinePriceExplanation>(`/api/v1/sales-invoices/${id}/lines/${lineId}/price-explanation`)
      .then(r => r.data),
};

export interface LinePriceExplanation {
  lineId: string; lineNo: number; lineType: string;
  description: string; sku?: string | null; batchNo?: string | null;
  qty: number; rate: number; discountAmount: number; taxAmount: number; lineTotal: number;

  /** What was written down at the time (W5). Null on lines billed before the snapshot shipped. */
  listRate?: number | null;
  priceSource?: PriceSource | null;
  priceSourceId?: string | null;
  priceSourceName?: string | null;
  recordedExplanation: string;

  discountReason?: string | null;
  discountApprovedBy?: string | null;
  discountApprovedByName?: string | null;
  discountApprovedAt?: string | null;

  billedOn: string; billType: string; invoiceStatus: string;
  customerId?: string | null; customerName?: string | null;
  admissionTypeName?: string | null;
  personTypeName?: string | null;
  insuranceSchemeName?: string | null;

  /** What the same question answers today — never mixed with the recorded answer above. */
  todayRate?: number | null;
  todaySource?: string | null;
  todayExplanation?: string | null;
  /** Why re-resolving failed. An answer in itself: the item may no longer be priceable. */
  todayProblem?: string | null;
  /** True when today's rate differs from the one billed. The reason to look. */
  differs: boolean;
}

// ---------- Item picker (S2.1, USE_CASE_CATALOG Part A1) ----------
export type PickerStockPolicy = "Issue" | "Procure" | "Source" | "Any";

export interface ItemPickerRow {
  id: string; sku: string; name: string; genericName?: string;
  form?: string; packaging?: string; strength?: string; manufacturer?: string;
  category?: string; barcode?: string;
  isScheduleDrug: boolean; isOnFormulary: boolean;
  /** H, H1 or X (W21). Only H1 and X need a prescriber recorded at the counter. */
  scheduleCategory?: string | null;
  availableQty: number; suggestedQty?: number; mrp?: number;
  isOutOfStock: boolean; isLowStock: boolean;
  /** Look-alike/sound-alike flag — item has at least one active LASA partner. */
  hasLasa?: boolean;
  /** Linked generic master row; drives the monograph pop-out. Absent = nothing to show. */
  genericNameId?: string | null;
}

export const itemPickerApi = {
  picker: (params: {
    stockPolicy: PickerStockPolicy; warehouseId: string;
    q?: string; category?: string; inStockOnly?: boolean; formularyOnly?: boolean;
    page?: number; pageSize?: number;
  }) => api.get<ItemPickerRow[]>("/api/v1/items/picker", { params }).then(r => r.data),
  byBarcode: (code: string) =>
    api.get<ItemPickerRow>(`/api/v1/items/by-barcode/${encodeURIComponent(code)}`).then(r => r.data),
};

// ---------- Users & Roles ----------
export interface UserBranchAccessDto {
  id: string; branchId: string; branchName: string;
  roleId: string; roleName: string; warehouseIds?: string[];
}

export interface UserListDto {
  id: string; email: string; fullName: string;
  isActive: boolean; isLockedOut: boolean; mfaEnabled: boolean;
  createdAt: string; branchAccessCount: number;
}

export interface UserDetailDto extends UserListDto {
  passwordExpiresAt?: string;
  branchAccess: UserBranchAccessDto[];
}

export interface RoleDto {
  id: string; name: string; code: string; isSystem: boolean; permissionCount: number;
}

export interface BranchDto {
  id: string; code: string; name: string; isActive: boolean;
}

export interface CreateUserBranchAccessRequest {
  branchId: string; roleId: string; warehouseIds?: string[];
}

export interface CreateUserRequest {
  email: string; fullName: string; password: string; mfaEnabled: boolean;
  branchAccess: CreateUserBranchAccessRequest[];
}

export interface UpdateUserRequest {
  id: string; fullName: string; isActive: boolean; mfaEnabled: boolean;
  branchAccess: CreateUserBranchAccessRequest[];
}

export const usersApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<UserListDto>>("/api/v1/users", { params }).then(r => r.data),
  get: (id: string) => api.get<UserDetailDto>(`/api/v1/users/${id}`).then(r => r.data),
  create: (payload: CreateUserRequest) =>
    api.post<{ id: string }>("/api/v1/users", payload).then(r => r.data),
  update: (id: string, payload: UpdateUserRequest) =>
    api.put(`/api/v1/users/${id}`, payload),
  resetPassword: (id: string, newPassword: string) =>
    api.post(`/api/v1/users/${id}/reset-password`, { newPassword }),
  unlock: (id: string) => api.post(`/api/v1/users/${id}/unlock`),
};

// rolesApi (write + matrix) is exported above near the S6 admin block.

export const branchesApi = {
  list: () => api.get<BranchDto[]>("/api/v1/branches").then(r => r.data),
};

// ---------- UoM ----------
export interface UomDto { id: string; code: string; name: string }

export const uomsApi = {
  list: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<UomDto>>("/api/v1/uoms", { params }).then(r => r.data),
  create: (payload: { code: string; name: string }) =>
    api.post<{ id: string }>("/api/v1/uoms", payload).then(r => r.data),
  update: (id: string, payload: { name: string }) =>
    api.put(`/api/v1/uoms/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/uoms/${id}`),
};

// ---------- Service master ----------
export interface ServiceDto {
  id: string; code: string; name: string; sacCode?: string;
  defaultRate: number; gstRate: number;
  glAccountId?: string | null; glAccountCode?: string | null; glAccountName?: string | null;
  serviceGroupId?: string | null; serviceGroupName?: string | null;
  unitOfMeasureId?: string | null; unitName?: string | null;
  /**
   * The head a posting actually uses once the tenant's ServiceAccountMappingMode is applied.
   * Differs from glAccountId for tenants that map the chart of accounts on the service group.
   */
  effectiveGlAccountId?: string | null;
  /** Rate effective today (resolved from the rate history; falls back to defaultRate). */
  currentRate: number;
  isActive: boolean;
}

export interface ServiceRateDto {
  id: string; serviceMasterId: string; rate: number; effectiveFrom: string; isActive: boolean;
}

export const servicesApi = {
  list: (params: { search?: string; isActive?: boolean; serviceGroupId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ServiceDto>>("/api/v1/services", { params }).then(r => r.data),
  get: (id: string) => api.get<ServiceDto>(`/api/v1/services/${id}`).then(r => r.data),
  create: (payload: Partial<ServiceDto>) =>
    api.post<{ id: string }>("/api/v1/services", payload).then(r => r.data),
  update: (id: string, payload: Partial<ServiceDto>) =>
    api.put(`/api/v1/services/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/services/${id}`),
  rates: (id: string) => api.get<ServiceRateDto[]>(`/api/v1/services/${id}/rates`).then(r => r.data),
  addRate: (id: string, rate: number, effectiveFrom: string) =>
    api.post<{ id: string }>(`/api/v1/services/${id}/rates`, { rate, effectiveFrom }).then(r => r.data),
  setRateActive: (rateId: string, isActive: boolean) =>
    api.put(`/api/v1/services/rates/${rateId}/active`, { isActive }),
};

// ---------- Service groups ----------
export interface ServiceGroupDto {
  id: string; code: string; name: string; description?: string | null;
  glAccountId?: string | null; glAccountCode?: string | null; glAccountName?: string | null;
  displayOrder?: number | null;
  /** How many service items roll up to this group — drives the delete guard on the UI. */
  serviceItemCount: number;
  isActive: boolean;
  /** W23 — where this group sits in the tree. Null parent = a department. */
  parentId?: string | null;
  parentName?: string | null;
  /** Groups directly beneath it. A parent with children cannot be deleted. */
  childCount: number;
}
export interface ServiceGroupPayload {
  code: string; name: string; description?: string | null;
  glAccountId?: string | null; displayOrder?: number | null; isActive?: boolean;
}

export const serviceGroupsApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ServiceGroupDto>>("/api/v1/service-groups", { params }).then(r => r.data),
  get: (id: string) => api.get<ServiceGroupDto>(`/api/v1/service-groups/${id}`).then(r => r.data),
  create: (payload: ServiceGroupPayload) =>
    api.post<{ id: string }>("/api/v1/service-groups", payload).then(r => r.data),
  update: (id: string, payload: ServiceGroupPayload) =>
    api.put(`/api/v1/service-groups/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/service-groups/${id}`),
  /**
   * W23 — service revenue by group, rolled up the tree. Each group reports what was billed
   * directly against it and what its whole subtree earned.
   */
  revenue: (fromDate: string, toDate: string) =>
    api.get<ServiceRevenueRow[]>("/api/v1/service-groups/revenue", {
      params: { fromDate, toDate },
    }).then(r => r.data),
};

export interface ServiceRevenueRow {
  groupId: string; parentId?: string | null; code: string; name: string;
  /** Depth in the tree, so a flat list can still be read as one. */
  level: number;
  ownLineCount: number; ownRevenue: number;
  /** This group and everything under it. */
  rolledUpLineCount: number; rolledUpRevenue: number;
}

// ---------- Service charge rate dimensions ----------
// Admission type and person type share one shape; the generic master page renders both.
export interface CodeNameMasterDto {
  id: string; code: string; name: string; description?: string | null;
  displayOrder?: number | null; isActive: boolean;
}
export interface CodeNameMasterPayload {
  code: string; name: string; description?: string | null;
  displayOrder?: number | null; isActive?: boolean;
}
export interface CodeNameMasterListParams {
  search?: string; isActive?: boolean; page?: number; pageSize?: number;
}
export interface CodeNameMasterApi {
  list: (params: CodeNameMasterListParams) => Promise<PaginatedList<CodeNameMasterDto>>;
  create: (payload: CodeNameMasterPayload) => Promise<{ id: string }>;
  update: (id: string, payload: CodeNameMasterPayload) => Promise<unknown>;
  delete: (id: string) => Promise<unknown>;
}

function codeNameMasterApi(path: string): CodeNameMasterApi {
  return {
    list: (params = {}) =>
      api.get<PaginatedList<CodeNameMasterDto>>(path, { params }).then(r => r.data),
    create: (payload) => api.post<{ id: string }>(path, payload).then(r => r.data),
    update: (id, payload) => api.put(`${path}/${id}`, { id, ...payload }),
    delete: (id) => api.delete(`${path}/${id}`),
  };
}

/** General / Insurance / Corporate / Emergency — the basis the patient was admitted on. */
export const admissionTypesApi = codeNameMasterApi("/api/v1/admission-types");
/** VIP / Management / Employee / Owner — who the patient is to the hospital. */
export const personTypesApi = codeNameMasterApi("/api/v1/person-types");

export interface InsuranceSchemeDto {
  id: string; code: string; name: string; payerName?: string | null;
  customerId?: string | null; customerName?: string | null;
  description?: string | null; isActive: boolean;
  // Contract terms (W12). Null throughout on a scheme that carries no dated agreement.
  /** Administrator handling claims, where that differs from the insurer in payerName. */
  tpaName?: string | null;
  contractFrom?: string | null;
  contractTo?: string | null;
  /** The rate card negotiated under this agreement. */
  tariffId?: string | null;
  tariffCode?: string | null;
  /** Patient's out-of-pocket share; defaults onto bills raised under this scheme. */
  coPayPct?: number | null;
  discountPct?: number | null;
  creditDays?: number | null;
  claimSubmissionDays?: number | null;
  exclusions?: string | null;
}
export interface InsuranceSchemePayload {
  code: string; name: string; payerName?: string | null;
  customerId?: string | null; description?: string | null; isActive?: boolean;
}

export const insuranceSchemesApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<InsuranceSchemeDto>>("/api/v1/insurance-schemes", { params }).then(r => r.data),
  get: (id: string) => api.get<InsuranceSchemeDto>(`/api/v1/insurance-schemes/${id}`).then(r => r.data),
  create: (payload: InsuranceSchemePayload) =>
    api.post<{ id: string }>("/api/v1/insurance-schemes", payload).then(r => r.data),
  update: (id: string, payload: InsuranceSchemePayload) =>
    api.put(`/api/v1/insurance-schemes/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/insurance-schemes/${id}`),
};

export interface CurrencyDto {
  id: string; code: string; name: string; symbol?: string | null;
  decimalPlaces: number; isBaseCurrency: boolean; isActive: boolean;
}
export interface CurrencyPayload {
  code: string; name: string; symbol?: string | null;
  decimalPlaces: number; isBaseCurrency: boolean; isActive?: boolean;
}

export const currenciesApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<CurrencyDto>>("/api/v1/currencies", { params }).then(r => r.data),
  get: (id: string) => api.get<CurrencyDto>(`/api/v1/currencies/${id}`).then(r => r.data),
  create: (payload: CurrencyPayload) =>
    api.post<{ id: string }>("/api/v1/currencies", payload).then(r => r.data),
  update: (id: string, payload: CurrencyPayload) =>
    api.put(`/api/v1/currencies/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/currencies/${id}`),
};

// ---------- Exchange rates (W25) ----------
// The piece that was missing: currencyId is already snapshotted on every charge row and the
// currency master already knows which one is the base, so a card in a contract currency could
// always be stored — it just could not be reported alongside anything else.

export interface ExchangeRateDto {
  id: string;
  currencyId: string;
  currencyCode: string;
  currencyName: string;
  /** Units of the base currency per ONE unit of this one. 83, not 0.012. */
  rateToBase: number;
  effectiveFrom: string;
  source?: string | null;
  baseCurrencyCode?: string | null;
  /** True for the row currently in force for its currency. */
  isCurrent: boolean;
}

export interface ExchangeRatePayload {
  currencyId: string;
  rateToBase: number;
  effectiveFrom: string;
  source?: string | null;
}

export const exchangeRatesApi = {
  list: (params: { currencyId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ExchangeRateDto>>("/api/v1/exchange-rates", { params }).then(r => r.data),
  /** Rates are never edited — a correction is a new row from a new date. */
  create: (payload: ExchangeRatePayload) =>
    api.post<{ id: string }>("/api/v1/exchange-rates", payload).then(r => r.data),
  /** Refused once a later rate supersedes this one: it covered a period that may be reported on. */
  delete: (id: string) => api.delete(`/api/v1/exchange-rates/${id}`),
};

// ---------- Service charges (the rate card) ----------
export interface ServiceChargeDto {
  id: string;
  /** null = applies to every sub-tenant; set = priced for one branch. */
  branchId?: string | null;
  serviceGroupId?: string | null; serviceGroupName?: string | null;
  serviceItemId: string; serviceItemName: string;
  unitOfMeasureId?: string | null; unitName?: string | null;
  rate: number; professionalCharge: number; facilityCharge: number;
  effectiveFrom: string; effectiveTo?: string | null;
  admissionTypeId?: string | null; admissionTypeName?: string | null;
  personTypeId?: string | null; personTypeName?: string | null;
  insuranceSchemeId?: string | null; insuranceSchemeName?: string | null;
  isTaxable: boolean; taxRatePct: number;
  currencyId?: string | null; currencyCode?: string | null;
  isActive: boolean;
  /** Rate card this row belongs to (W11). Null = standalone, which is the ordinary case. */
  tariffId?: string | null;
  tariffCode?: string | null;
  tariffName?: string | null;
  /** Live state of that card — a row on a Draft or Superseded card prices nothing. */
  tariffStatus?: TariffStatus | null;
  /**
   * How this row arrives at its rate (W13). When it is not `Absolute`, `rate` is 0 and carries no
   * meaning — the amount is worked out at billing time from the base card.
   */
  derivation: RateDerivation;
  derivedFromTariffId?: string | null; derivedFromTariffCode?: string | null;
  /** Signed — −10 is ten percent under the base card, +15 is fifteen percent over it. */
  derivationAdjustmentPct: number;
}

export interface ServiceChargePayload {
  serviceItemId: string;
  rate: number; effectiveFrom: string; effectiveTo?: string | null;
  professionalCharge: number; facilityCharge: number;
  unitOfMeasureId?: string | null;
  admissionTypeId?: string | null; personTypeId?: string | null; insuranceSchemeId?: string | null;
  isTaxable: boolean; taxRatePct: number;
  currencyId?: string | null;
  branchId?: string | null;
  isActive?: boolean;
  /** Rate card this row belongs to (W11). Null keeps it standalone. */
  tariffId?: string | null;
  /**
   * How the row is priced (W13). Omit for a typed amount. `BaseTariff` charges the same service's
   * rate on `derivedFromTariffId`, moved by `derivationAdjustmentPct`; services have no MRP, so
   * `Mrp` is refused on this side.
   */
  derivation?: RateDerivation;
  derivedFromTariffId?: string | null;
  derivationAdjustmentPct?: number;
}

/** Where the resolved rate came from — a charge row, the legacy rate history, or the item default. */
export type ResolvedChargeSource = "ServiceCharge" | "ServiceRate" | "ServiceDefault";

export interface ResolvedServiceChargeDto {
  serviceItemId: string; serviceItemName: string;
  serviceChargeId?: string | null; source: ResolvedChargeSource;
  rate: number; professionalCharge: number; facilityCharge: number;
  isTaxable: boolean; taxRatePct: number; taxAmount: number; grossAmount: number;
  currencyId?: string | null; currencyCode?: string | null; unitName?: string | null;
  admissionTypeId?: string | null; personTypeId?: string | null; insuranceSchemeId?: string | null;
  effectiveFrom?: string | null; effectiveTo?: string | null;
  glAccountId?: string | null; glAccountCode?: string | null; glAccountName?: string | null;
}

export interface ServiceChargeListParams {
  serviceItemId?: string; serviceGroupId?: string;
  admissionTypeId?: string; personTypeId?: string; insuranceSchemeId?: string;
  /** Narrows to rows live on this date; omit for the full history including closed rows. */
  effectiveOn?: string;
  isActive?: boolean; search?: string;
  page?: number; pageSize?: number;
}

export const serviceChargesApi = {
  list: (params: ServiceChargeListParams = {}) =>
    api.get<PaginatedList<ServiceChargeDto>>("/api/v1/service-charges", { params }).then(r => r.data),
  get: (id: string) => api.get<ServiceChargeDto>(`/api/v1/service-charges/${id}`).then(r => r.data),
  create: (payload: ServiceChargePayload) =>
    api.post<{ id: string }>("/api/v1/service-charges", payload).then(r => r.data),
  update: (id: string, payload: ServiceChargePayload) =>
    api.put(`/api/v1/service-charges/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/service-charges/${id}`),
  /** Closes the current row and opens its successor — the supported way to change a live rate. */
  revise: (id: string, body: {
    newRate: number; effectiveFrom: string; professionalCharge: number; facilityCharge: number;
  }) => api.post<{ id: string }>(`/api/v1/service-charges/${id}/revise`, body).then(r => r.data),
  resolve: (params: {
    serviceItemId: string; on?: string;
    admissionTypeId?: string; personTypeId?: string; insuranceSchemeId?: string; currencyId?: string;
  }) => api.get<ResolvedServiceChargeDto>("/api/v1/service-charges/resolve", { params }).then(r => r.data),
};

// ---------- Item Category + Form masters ----------
// itemType scopes a row to one item type; null/undefined = global (shown for every type).
export interface NamedMasterDto { id: string; name: string; itemType?: string | null }
export interface NamedMasterListParams { search?: string; itemType?: string; page?: number; pageSize?: number }
export interface NamedMasterPayload { name: string; itemType?: string | null }

export const itemCategoriesApi = {
  list: (params: NamedMasterListParams = {}) =>
    api.get<PaginatedList<NamedMasterDto>>("/api/v1/item-categories", { params }).then(r => r.data),
  create: (payload: NamedMasterPayload) =>
    api.post<{ id: string }>("/api/v1/item-categories", payload).then(r => r.data),
  update: (id: string, payload: NamedMasterPayload) =>
    api.put(`/api/v1/item-categories/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/item-categories/${id}`),
};

export const itemFormsApi = {
  list: (params: NamedMasterListParams = {}) =>
    api.get<PaginatedList<NamedMasterDto>>("/api/v1/item-forms", { params }).then(r => r.data),
  create: (payload: NamedMasterPayload) =>
    api.post<{ id: string }>("/api/v1/item-forms", payload).then(r => r.data),
  update: (id: string, payload: NamedMasterPayload) =>
    api.put(`/api/v1/item-forms/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/item-forms/${id}`),
};

/** Route of administration master — backs the Route dropdown on the item form. */
export const itemRoutesApi = {
  list: (params: NamedMasterListParams = {}) =>
    api.get<PaginatedList<NamedMasterDto>>("/api/v1/item-routes", { params }).then(r => r.data),
  create: (payload: NamedMasterPayload) =>
    api.post<{ id: string }>("/api/v1/item-routes", payload).then(r => r.data),
  update: (id: string, payload: NamedMasterPayload) =>
    api.put(`/api/v1/item-routes/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/item-routes/${id}`),
};

// ---------- Generic name master ----------
/**
 * Optional monograph fields on a generic. Every one may be absent — they are captured from the
 * pack insert / supplier sheet when available and never required to save the generic.
 */
export interface GenericNameDetails {
  countryOfOrigin?: string | null;
  ingredients?: string | null;
  directionsForUse?: string | null;
  safetyInformation?: string | null;
  therapeuticClassification?: string | null;
  /** Regulatory schedule, e.g. "Schedule H1". Free text, like the rest of this block. */
  schedule?: string | null;
  composition?: string | null;
  contraindications?: string | null;
  sideEffects?: string | null;
  precautionsAndWarnings?: string | null;
  uses?: string | null;
}

/** List/picker row — `hasDetails` flags rows that carry monograph information. */
export interface GenericNameDto extends NamedMasterDto { hasDetails: boolean }

/** Single generic with its monograph fields, from GET /generic-names/{id}. */
export interface GenericNameDetailDto extends GenericNameDetails { id: string; name: string }

export interface GenericNamePayload { name: string; details?: GenericNameDetails | null }

// Name-only master, plus the optional monograph block.
export const genericNamesApi = {
  list: (params: NamedMasterListParams = {}) =>
    api.get<PaginatedList<GenericNameDto>>("/api/v1/generic-names", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<GenericNameDetailDto>(`/api/v1/generic-names/${id}`).then(r => r.data),
  create: (payload: GenericNamePayload) =>
    api.post<{ id: string }>("/api/v1/generic-names", { name: payload.name, details: payload.details ?? null })
      .then(r => r.data),
  update: (id: string, payload: GenericNamePayload) =>
    api.put(`/api/v1/generic-names/${id}`, { id, name: payload.name, details: payload.details ?? null }),
  delete: (id: string) => api.delete(`/api/v1/generic-names/${id}`),
};

export const supplyTypesApi = {
  list: (params: NamedMasterListParams = {}) =>
    api.get<PaginatedList<NamedMasterDto>>("/api/v1/supply-types", { params }).then(r => r.data),
  create: (payload: NamedMasterPayload) =>
    api.post<{ id: string }>("/api/v1/supply-types", { name: payload.name }).then(r => r.data),
  update: (id: string, payload: NamedMasterPayload) =>
    api.put(`/api/v1/supply-types/${id}`, { id, name: payload.name }),
  delete: (id: string) => api.delete(`/api/v1/supply-types/${id}`),
};

// ---------- PO state transitions ----------
export const purchaseOrderStateApi = {
  submit:  (id: string, rowVersion: string, comment?: string) =>
    api.post(`/api/v1/purchase-orders/${id}/submit`, { comment }, { headers: { "If-Match": `"${rowVersion}"` } }),
  approve: (id: string, rowVersion: string, comment?: string) =>
    api.post(`/api/v1/purchase-orders/${id}/approve`, { comment }, { headers: { "If-Match": `"${rowVersion}"` } }),
  reject:  (id: string, rowVersion: string, reason: string) =>
    api.post(`/api/v1/purchase-orders/${id}/reject`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  cancel:  (id: string, rowVersion: string, reason: string) =>
    api.post(`/api/v1/purchase-orders/${id}/cancel`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
};

// ---------- PO / SI detail shapes (loose - backend DTOs are flat records) ----------
export interface PurchaseOrderLineDetailDto {
  id: string; itemId: string; itemName: string; sku: string;
  qty: number; rate: number; taxAmount: number; receivedQty: number;
  lineTotal: number; pendingQty: number;
  poLineSource?: string;
}
export interface PurchaseOrderDetailDto {
  id: string; documentNo: string; documentDate: string; status: string;
  vendorId: string; vendorName: string;
  warehouseId: string; warehouseName?: string;
  expectedDeliveryDate?: string; vendorRef?: string; notes?: string;
  subTotal?: number; taxAmount?: number; totalAmount: number;
  lines: PurchaseOrderLineDetailDto[]; rowVersion: string;
}

export interface SalesInvoiceLineDetailDto {
  id: string; lineNo: number; lineType: LineType;
  itemId?: string; itemName?: string; sku?: string;
  serviceId?: string; serviceName?: string;
  description?: string; prescriptionRef?: string;
  substitutedFromItemId?: string; substitutedFromItemName?: string;
  substituteReasonCode?: string;
  qty: number; rate: number; taxAmount: number; discountAmount: number;
  lineTotal: number;
  /** Linked generic master row; drives the monograph pop-out. Absent = nothing to show. */
  genericNameId?: string | null;
}
export interface SalesInvoicePaymentDto {
  id: string; mode: PaymentLineRequest["mode"];
  amountReceived: number; reference?: string; paidAt: string;
}
export interface SalesInvoiceDetailDto {
  id: string; documentNo: string; documentDate: string; status: string; billType: string;
  customerId?: string; customerName?: string; customerPhone?: string;
  warehouseId: string;
  subTotal: number; taxAmount: number; discountAmount: number; netAmount: number;
  journalEntryId?: string;
  notes?: string;
  lines: SalesInvoiceLineDetailDto[];
  payments: SalesInvoicePaymentDto[];
}

// ---------- Stock Adjustments ----------
export interface StockAdjustmentLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string; expiryDate?: string;
  qtyDelta: number; lineReason?: string;
}
export interface StockAdjustmentListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  warehouseId: string; warehouseName: string; reason: string;
  totalAmount: number; lineCount: number;
}
export interface StockAdjustmentDetailDto extends StockAdjustmentListDto {
  notes?: string; lines: StockAdjustmentLineDto[];
}
export interface CreateStockAdjustmentLineRequest {
  itemId: string; batchId: string; qtyDelta: number; reason?: string;
}
export interface CreateStockAdjustmentRequest {
  warehouseId: string; documentDate: string; reason: string; notes?: string;
  lines: CreateStockAdjustmentLineRequest[];
}

export const stockAdjustmentsApi = {
  list: (params: { status?: string; warehouseId?: string; fromDate?: string; toDate?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<StockAdjustmentListDto>>("/api/v1/stock-adjustments", { params }).then(r => r.data),
  get: (id: string) => api.get<StockAdjustmentDetailDto>(`/api/v1/stock-adjustments/${id}`).then(r => r.data),
  create: (payload: CreateStockAdjustmentRequest) =>
    api.post<{ id: string }>("/api/v1/stock-adjustments", payload).then(r => r.data),
  post: (id: string) => api.post(`/api/v1/stock-adjustments/${id}/post`),
};

// ---------- S4 — Inventory Ops (Write-off / Internal Transfer / Stock Take / Near Expiry) ----------
export type WriteOffReason = "Expired" | "Damaged" | "Lost" | "QcReject";

export interface StockWriteOffListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  warehouseId: string; warehouseName: string; reason?: string;
  totalQty: number; lineCount: number;
}
export interface StockWriteOffLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string; expiryDate?: string;
  qty: number; reasonCode: WriteOffReason; lineNotes?: string;
}
export interface StockWriteOffDetailDto extends StockWriteOffListDto {
  notes?: string; rowVersion: string; lines: StockWriteOffLineDto[];
}
export interface CreateStockWriteOffLineRequest {
  itemId: string; batchId: string; qty: number;
  reasonCode: WriteOffReason; lineNotes?: string;
}
export interface CreateStockWriteOffRequest {
  warehouseId: string; documentDate: string;
  reason?: string; notes?: string; lines: CreateStockWriteOffLineRequest[];
}
export const stockWriteOffsApi = {
  list: (params: { status?: string; warehouseId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<StockWriteOffListDto>>("/api/v1/stock/write-offs", { params }).then(r => r.data),
  get: (id: string) => api.get<StockWriteOffDetailDto>(`/api/v1/stock/write-offs/${id}`).then(r => r.data),
  create: (payload: CreateStockWriteOffRequest) =>
    api.post<{ id: string }>("/api/v1/stock/write-offs", payload).then(r => r.data),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/stock/write-offs/${id}/post`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

export interface InternalTransferListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  sourceWarehouseId: string; sourceWarehouseName: string;
  destWarehouseId: string; destWarehouseName: string;
  lineCount: number; totalQty: number; inTransitQty: number;
}
export interface InternalTransferLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string;
  qty: number; receivedQty: number; inTransitQty: number;
}
export interface InternalTransferDetailDto extends InternalTransferListDto {
  reason?: string; notes?: string; rowVersion: string; lines: InternalTransferLineDto[];
}
export interface CreateInternalTransferLineRequest { itemId: string; batchId: string; qty: number }
export interface CreateInternalTransferRequest {
  sourceWarehouseId: string; destWarehouseId: string;
  documentDate: string; reason?: string; notes?: string;
  lines: CreateInternalTransferLineRequest[];
}
export interface ReceiveInternalTransferRequest {
  lines: { lineId: string; receivedQty: number }[];
}
export const internalTransfersApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<InternalTransferListDto>>("/api/v1/internal-transfers", { params }).then(r => r.data),
  get: (id: string) => api.get<InternalTransferDetailDto>(`/api/v1/internal-transfers/${id}`).then(r => r.data),
  create: (payload: CreateInternalTransferRequest) =>
    api.post<{ id: string }>("/api/v1/internal-transfers", payload).then(r => r.data),
  dispatch: (id: string, rowVersion: string) =>
    api.post(`/api/v1/internal-transfers/${id}/dispatch`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  receive: (id: string, payload: ReceiveInternalTransferRequest, rowVersion: string) =>
    api.post(`/api/v1/internal-transfers/${id}/receive`, payload, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

export interface StockTakeListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  warehouseId: string; warehouseName: string;
  lineCount: number; totalVariance: number;
}
export interface StockTakeLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string;
  systemQty: number; countedQty: number; varianceQty: number;
  lineNotes?: string;
}
export interface StockTakeDetailDto extends StockTakeListDto {
  notes?: string; rowVersion: string; lines: StockTakeLineDto[];
}
export interface CreateStockTakeRequest {
  warehouseToCountId: string; documentDate: string; notes?: string;
  batchIds?: string[];
}
export const stockTakesApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<StockTakeListDto>>("/api/v1/stock-takes", { params }).then(r => r.data),
  get: (id: string) => api.get<StockTakeDetailDto>(`/api/v1/stock-takes/${id}`).then(r => r.data),
  create: (payload: CreateStockTakeRequest) =>
    api.post<{ id: string }>("/api/v1/stock-takes", payload).then(r => r.data),
  updateCounts: (id: string, lines: { lineId: string; countedQty: number; lineNotes?: string }[]) =>
    api.put(`/api/v1/stock-takes/${id}/counts`, { lines }),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/stock-takes/${id}/post`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

export interface NearExpiryRow {
  batchId: string; itemId: string; itemSku: string; itemName: string;
  form?: string; strength?: string;
  batchNo: string; expiryDate: string; daysToExpiry: number;
  warehouseId: string; warehouseName: string;
  onHandQty: number; costPrice: number; estimatedExposure: number;
}
export const nearExpiryApi = {
  list: (windowDays: number = 30, warehouseId?: string) =>
    api.get<NearExpiryRow[]>("/api/v1/inventory/near-expiry", {
      params: { windowDays, warehouseId },
    }).then(r => r.data),
};

// ---------- S4 — Reorder suggestions + bulk PO ----------
/** Which rule produced `reorderPoint` — set by the `inventory.predictive_reorder` flag. */
export type ReorderBasis = "Static" | "Predictive";
export interface ReorderSuggestionRow {
  itemId: string; sku: string; name: string; genericName?: string;
  form?: string; packaging?: string; strength?: string;
  availableQty: number;
  /** Already on a live PO for this warehouse and not yet received. */
  onOrderQty: number;
  /** availableQty + onOrderQty — what `reorderPoint` was compared against. */
  inventoryPositionQty: number;
  reorderPoint: number; suggestedQty: number;
  lastPurchaseRate?: number; lastVendorId?: string; lastVendorName?: string;
  isOutOfStock: boolean;
  basis: ReorderBasis;
  /** Predictive rows only. */
  avgDailyConsumption?: number;
  /** Predictive rows only; absent also means nothing is moving (cover is effectively infinite). */
  daysOfCover?: number;
  vedClass?: "Vital" | "Essential" | "Desirable";
  fsnClass?: "Fast" | "Slow" | "NonMoving";
}
export type ReorderSupplierStrategy = "BestGraded" | "LastSupplier" | "BestRate6Mo" | "SpecificVendor";
export interface CreatePOsFromReorderRequest {
  warehouseId: string; itemIds: string[];
  strategy: ReorderSupplierStrategy; vendorIdOverride?: string;
}
export interface CreatedPoSummary {
  purchaseOrderId: string; vendorId: string; vendorName: string;
  lineCount: number; totalQty: number; estimatedTotal: number;
}
export interface CreatePOsFromReorderResponse {
  createdPurchaseOrders: CreatedPoSummary[];
  unresolvedItemIds: string[];
  /** Selected items that no longer sit below their reorder point — nothing was ordered for these. */
  alreadyCoveredItemIds: string[];
}
export const reorderApi = {
  suggestions: (warehouseId: string) =>
    api.get<ReorderSuggestionRow[]>("/api/v1/inventory/reorder-suggestions", {
      params: { warehouseId },
    }).then(r => r.data),
  createPOs: (payload: CreatePOsFromReorderRequest) =>
    api.post<CreatePOsFromReorderResponse>("/api/v1/purchase-orders/from-reorder-suggestions", payload)
       .then(r => r.data),
};

// ---------- S11 — Hospital Ward / OT / CSSD ----------
export interface WardIssueListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  uhid: string; wardCode?: string; isEmergency: boolean;
  wardWarehouseId: string; lineCount: number;
}
export interface WardIssueLineReadDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string; qty: number; lineNotes?: string;
}
export interface WardIssueDetailDto {
  id: string; documentNo: string; documentDate: string; status: string;
  uhid: string; admissionId?: string; wardCode?: string;
  wardWarehouseId: string; isEmergency: boolean; reason?: string;
  rowVersion: string; lines: WardIssueLineReadDto[];
}
export interface CreateWardIssueLineDto { itemId: string; batchId: string; qty: number; lineNotes?: string }
export interface CreateWardIssueRequest {
  uhid: string; admissionId?: string; wardCode?: string; wardWarehouseId: string;
  isEmergency: boolean; reason?: string; lines: CreateWardIssueLineDto[];
  /** High-severity LASA items the operator confirmed; without these the server refuses the issue. */
  lasaAcknowledgedItemIds?: string[];
}
export const wardIssuesApi = {
  list: (params: { status?: string; uhid?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<WardIssueListDto>>("/api/v1/ward-issues", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<WardIssueDetailDto>(`/api/v1/ward-issues/${id}`).then(r => r.data),
  create: (payload: CreateWardIssueRequest) =>
    api.post<{ id: string }>("/api/v1/ward-issues", payload).then(r => r.data),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/ward-issues/${id}/post`, undefined, { headers: { "If-Match": rowVersion } }),
};

export interface OtConsumptionListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  uhid: string; procedureCode: string; surgeon?: string;
  lineCount: number; implantCount: number;
}
export interface OtConsumptionLineReadDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; qty: number; isImplant: boolean;
}
export interface ImplantReadDto { id: string; itemId: string; udi: string; implantedAt: string }
export interface OtConsumptionDetailDto {
  id: string; documentNo: string; documentDate: string; status: string;
  uhid: string; admissionId?: string; procedureCode: string; surgeon?: string;
  procedureAt?: string; otWarehouseId: string; surgicalKitId?: string;
  rowVersion: string; lines: OtConsumptionLineReadDto[]; implants: ImplantReadDto[];
}
export interface CreateOtConsumptionLineDto { itemId: string; batchId: string; qty: number; isImplant: boolean }
export interface CreateOtImplantDto { itemId: string; batchId: string; udi: string }
// ---------- Surgical kit (OT BOM) master ----------
export interface SurgicalKitRowDto {
  id: string; code: string; name: string;
  procedureCode?: string | null; isActive: boolean; itemCount: number;
}
export interface SurgicalKitItemDto {
  id: string; itemId: string; itemSku: string; itemName: string; qty: number; isImplant: boolean;
}
export interface SurgicalKitDetailDto {
  id: string; code: string; name: string;
  procedureCode?: string | null; isActive: boolean; items: SurgicalKitItemDto[];
}
export interface SurgicalKitItemInput { itemId: string; qty: number; isImplant: boolean }
export interface SurgicalKitPayload {
  code: string; name: string; procedureCode?: string | null; items: SurgicalKitItemInput[];
}

export const surgicalKitsApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<SurgicalKitRowDto>>("/api/v1/surgical-kits", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<SurgicalKitDetailDto>(`/api/v1/surgical-kits/${id}`).then(r => r.data),
  create: (payload: SurgicalKitPayload) =>
    api.post<{ id: string }>("/api/v1/surgical-kits", payload).then(r => r.data),
  /** The item list is replaced wholesale. */
  update: (id: string, payload: SurgicalKitPayload & { isActive: boolean }) =>
    api.put(`/api/v1/surgical-kits/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/surgical-kits/${id}`),
};

export interface CreateOtConsumptionRequest {
  uhid: string; admissionId?: string; procedureCode: string; otWarehouseId: string;
  surgicalKitId?: string; surgeon?: string; procedureAt?: string;
  lines: CreateOtConsumptionLineDto[]; implants: CreateOtImplantDto[];
  /** High-severity LASA items the operator confirmed; without these the server refuses the record. */
  lasaAcknowledgedItemIds?: string[];
}
export const otConsumptionsApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<OtConsumptionListDto>>("/api/v1/ot-consumptions", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<OtConsumptionDetailDto>(`/api/v1/ot-consumptions/${id}`).then(r => r.data),
  create: (payload: CreateOtConsumptionRequest) =>
    api.post<{ id: string }>("/api/v1/ot-consumptions", payload).then(r => r.data),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/ot-consumptions/${id}/post`, undefined, { headers: { "If-Match": rowVersion } }),
};

export type CssdCycleStatus = "InUse" | "Sterilizing" | "Sterile" | "Quarantined";
export interface CssdTrayDto {
  id: string; code: string; name: string; contents?: string;
  currentStatus: CssdCycleStatus; lastCycleAt?: string; isActive: boolean; cycleCount: number;
}
export interface CreateCssdTrayRequest { code: string; name: string; contents?: string }
export interface AddCycleRequest { status: CssdCycleStatus; notes?: string }
export const cssdTraysApi = {
  list: (params: { page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<CssdTrayDto>>("/api/v1/cssd-trays", { params }).then(r => r.data),
  create: (payload: CreateCssdTrayRequest) =>
    api.post<{ id: string }>("/api/v1/cssd-trays", payload).then(r => r.data),
  addCycle: (id: string, payload: AddCycleRequest) =>
    api.post(`/api/v1/cssd-trays/${id}/cycle`, payload),
};

// ---------- S12 — HealNTrack integration ----------
export type HealNTrackDirection = "Inbound" | "Outbound";
export type HealNTrackEventStatus = "Pending" | "Processed" | "Failed" | "Dead";
export interface HealNTrackEventRow {
  id: string;
  direction: HealNTrackDirection;
  eventType: string;
  uhid: string;
  admissionId?: string;
  status: HealNTrackEventStatus;
  attemptCount: number;
  at: string;
  lastError?: string;
}
export interface HealNTrackOpenChargeDto {
  salesInvoiceId: string; invoiceNo: string; invoiceDate: string;
  total: number; paid: number; outstanding: number;
}
export const healnTrackApi = {
  list: (params: {
    direction?: HealNTrackDirection; status?: HealNTrackEventStatus; uhid?: string;
    page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<HealNTrackEventRow>>("/api/v1/integrations/healntrack/v1/events",
      { params }).then(r => r.data),
  replay: (id: string) =>
    api.post(`/api/v1/integrations/healntrack/v1/events/${id}/replay`),
  openCharges: (uhid: string) =>
    api.get<HealNTrackOpenChargeDto[]>(
      `/api/v1/integrations/healntrack/v1/patients/${encodeURIComponent(uhid)}/open-charges`)
      .then(r => r.data),
};

// ---------- HealNTrack medication-order prescription queue (OP billing) ----------
/** `Partial` = some lines dispensed, balance still outstanding; `Billed` = every line done. */
export type PendingMedicationOrderStatus =
  "Ready" | "InBilling" | "Partial" | "Billed" | "Cancelled" | "Superseded";

export interface PendingMedicationOrderRow {
  id: string;
  medicationOrderId: number;
  revision: number;
  uhid: string;
  orderBy: number;
  /** Prescribing doctor's name; null on orders received before HealNTrack started sending it. */
  orderByName?: string | null;
  orderedOn: string;
  status: PendingMedicationOrderStatus;
  lineCount: number;
  unmappedCount: number;
  /** Lines fully dispensed — with lineCount this reads as "3 of 5". */
  dispensedLineCount: number;
  claimedAt?: string;
  patientName?: string | null;
  patientPhone?: string | null;
}

/** Shared by both queues: patient accepts a UHID or a mobile number, doctor a name or an id. */
export interface HealNTrackQueueFilters {
  patient?: string;
  doctor?: string;
  fromDate?: string;
  toDate?: string;
}

export interface BillingPrefillLine {
  medicationOrderTId: number;
  medicationId: number;
  itemId?: string;
  brandName?: string;
  qty: number;
  isMapped: boolean;
  itemName?: string;
  itemSku?: string;
  genericName?: string;
  form?: string;
  strength?: string;
  mrp?: number;
  /** Linked generic master row; drives the monograph pop-out. Absent = nothing to show. */
  genericNameId?: string | null;
}

export interface MedicationOrderBillingPrefill {
  id: string;
  medicationOrderId: number;
  revision: number;
  uhid: string;
  customerId?: string;
  customerName?: string;
  lines: BillingPrefillLine[];
}

export const medicationOrderQueueApi = {
  queue: (params: HealNTrackQueueFilters & {
    status?: PendingMedicationOrderStatus; page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<PendingMedicationOrderRow>>(
      "/api/v1/integrations/healntrack/v1/medication-orders/queue", { params }).then(r => r.data),
  claim: (id: string) =>
    api.post<MedicationOrderBillingPrefill>(
      `/api/v1/integrations/healntrack/v1/medication-orders/${id}/claim`).then(r => r.data),
  complete: (id: string, salesInvoiceId: string, invoiceNo?: string) =>
    api.post(`/api/v1/integrations/healntrack/v1/medication-orders/${id}/complete`,
      { salesInvoiceId, invoiceNo }),
  release: (id: string) =>
    api.post(`/api/v1/integrations/healntrack/v1/medication-orders/${id}/release`),
};

// ---------- HealNTrack diagnostic (lab/radiology) order queue ----------
export type DiagnosticDiscipline = "Lab" | "Radiology";
/** `Partial` = some tests turned into the dx_order, the rest still unmapped. */
export type PendingDiagnosticOrderStatus =
  "Ready" | "Processing" | "Partial" | "Processed" | "Cancelled";

export interface PendingDiagnosticOrderRow {
  id: string;
  discipline: DiagnosticDiscipline;
  externalOrderId: number;
  patientId: number;
  uhid?: string;
  orderBy: number;
  /** Ordering doctor's name; null on orders received before HealNTrack started sending it. */
  orderByName?: string | null;
  orderedOn: string;
  isExternal: boolean;
  status: PendingDiagnosticOrderStatus;
  lineCount: number;
  /** Lines turned into dx_order lines — with lineCount this reads as "2 of 4". */
  processedLineCount: number;
  diagnosticOrderId?: string;
  receivedAt: string;
  patientName?: string | null;
  patientPhone?: string | null;
}

export const diagnosticOrderQueueApi = {
  queue: (params: HealNTrackQueueFilters & {
    discipline?: DiagnosticDiscipline; status?: PendingDiagnosticOrderStatus;
  } = {}) =>
    api.get<PendingDiagnosticOrderRow[]>(
      "/api/v1/integrations/healntrack/v1/diagnostic-orders/queue", { params }).then(r => r.data),
  process: (id: string) =>
    api.post<{ pendingId: string; diagnosticOrderId: string }>(
      `/api/v1/integrations/healntrack/v1/diagnostic-orders/${id}/process`).then(r => r.data),
};

// ---------- S13 — GST e-invoice + e-way bill ----------
export type EInvoiceStatus = "Pending" | "Generated" | "Failed" | "Cancelled" | "NotRequired";
export type EWayBillStatus = "Pending" | "Generated" | "Failed" | "Expired" | "NotRequired";
export interface EInvoiceRow {
  id: string; salesInvoiceId: string; invoiceNo: string;
  status: EInvoiceStatus; irn?: string; ackNo?: string; ackDate?: string;
  attemptCount: number; lastAttemptAt?: string; manuallyOverridden: boolean; lastError?: string;
}
export interface EWayBillRow {
  id: string; shipmentId: string; transferDocumentNo: string;
  status: EWayBillStatus; ewbNo?: string; generatedAt?: string; validUntil?: string;
  attemptCount: number; lastAttemptAt?: string; manuallyEntered: boolean; lastError?: string;
}
export const gstApi = {
  listEInvoices: (params: { status?: EInvoiceStatus; q?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<EInvoiceRow>>("/api/v1/integrations/gst/einvoices", { params }).then(r => r.data),
  retryEInvoice: (id: string) =>
    api.post(`/api/v1/integrations/gst/einvoices/${id}/retry`),
  listEWayBills: (params: { status?: EWayBillStatus; q?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<EWayBillRow>>("/api/v1/integrations/gst/ewaybills", { params }).then(r => r.data),
  retryEWayBill: (id: string) =>
    api.post(`/api/v1/integrations/gst/ewaybills/${id}/retry`),
};

// ---------- S14 — VaidSetu, predictive reorder, narcotic register ----------
export interface VaidSetuInteraction {
  severity: "info" | "moderate" | "severe";
  pair: string[]; message: string; action: string;
}
export interface VaidSetuAllergy { substance: string; severity: string; message: string }
export interface VaidSetuResult {
  interactions: VaidSetuInteraction[]; allergies: VaidSetuAllergy[]; blocked: boolean;
}
export const vaidSetuApi = {
  check: (itemIds: string[], patientRef?: string) =>
    api.post<VaidSetuResult>("/api/v1/integrations/vaidsetu/check", { itemIds, patientRef })
      .then(r => r.data),
};

export type VedClass = "Vital" | "Essential" | "Desirable";
/** Derived from consumption in the report window — not stored on the item. */
export type FsnClass = "Fast" | "Slow" | "NonMoving";

export interface PredictiveReorderRow {
  itemId: string; sku: string; name: string; genericName?: string;
  /** On the shelf now, less active reservations — the same figure the reorder queue shows. */
  availableQty: number;
  /** Already on a live PO for this warehouse and not yet received. */
  onOrderQty: number;
  /** availableQty + onOrderQty — what the ROP is compared against. */
  inventoryPositionQty: number;
  avgDailyConsumption: number; leadTimeDays: number;
  /** The buyer's stored baseline, before criticality scaling. */
  baseSafetyStock: number;
  /** baseSafetyStock × VED factor × FSN factor — what the ROP actually used. */
  effectiveSafetyStock: number;
  predictedReorderPoint: number; daysOfCover?: number;
  suggestedQty: number; shouldReorder: boolean;
  vedClass?: VedClass; fsnClass: FsnClass; bufferFactor: number;
}
export const predictiveReorderApi = {
  list: (warehouseId: string, windowDays = 90, onlyBelowRop = true) =>
    api.get<PredictiveReorderRow[]>("/api/v1/inventory/predictive-reorder", {
      params: { warehouseId, windowDays, onlyBelowRop },
    }).then(r => r.data),
};

export interface NarcoticRegisterRow {
  at: string; itemId: string; sku: string; name: string; scheduleCategory?: string;
  batchNo: string; movement: string; qtyIn: number; qtyOut: number;
  runningBalance: number; referenceType?: string; referenceId?: string;
}
export const narcoticApi = {
  list: (params: { itemId?: string; from?: string; to?: string } = {}) =>
    api.get<NarcoticRegisterRow[]>("/api/v1/inventory/narcotic-register", { params }).then(r => r.data),
};

// ---------- Consultants and revenue share (W20) ----------

/** How a consultant's share of a line is expressed. */
export type RevenueShareType = "Percent" | "Amount";

export interface RevenueShareRuleDto {
  id: string;
  serviceId?: string | null; serviceName?: string | null;
  serviceGroupId?: string | null; serviceGroupName?: string | null;
  shareType: RevenueShareType;
  value: number;
  effectiveFrom: string; effectiveTo?: string | null;
  isActive: boolean;
  /** Higher wins: a named service beats its group, which beats a blanket rule. */
  specificity: number;
}

export interface ConsultantDto {
  id: string; code: string; name: string;
  specialty?: string | null; registrationNo?: string | null; phone?: string | null;
  /** Links inbound HealNTrack orders to this consultant. */
  healNTrackEmployeeId?: number | null;
  defaultSharePct?: number | null;
  isActive: boolean;
  ruleCount: number;
  rowVersion: string;
  shareRules: RevenueShareRuleDto[];
}

export interface UpsertShareRuleRequest {
  serviceId?: string | null;
  serviceGroupId?: string | null;
  shareType: RevenueShareType;
  value: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive?: boolean;
}

export interface UpsertConsultantRequest {
  code: string; name: string;
  specialty?: string | null; registrationNo?: string | null; phone?: string | null;
  healNTrackEmployeeId?: number | null;
  defaultSharePct?: number | null;
  isActive: boolean;
  shareRules: UpsertShareRuleRequest[];
}

export interface ConsultantPayoutLineDto {
  invoiceId: string; documentNo: string; documentDate: string;
  patientName?: string | null; uhid?: string | null;
  serviceId?: string | null; serviceName: string;
  qty: number; rate: number; discountAmount: number;
  lineNet: number; share: number;
  /** The rule that decided it, in words. */
  rule?: string | null;
}

export interface ConsultantPayoutServiceTotalDto {
  serviceName: string; lines: number; billed: number; share: number;
}

export interface ConsultantPayoutDto {
  consultantId: string; code: string; name: string; specialty?: string | null;
  fromDate: string; toDate: string;
  billCount: number; lineCount: number;
  billed: number; share: number;
  /** Share as a percentage of what was billed — where a renegotiation starts. */
  effectivePct: number;
  byService: ConsultantPayoutServiceTotalDto[];
  lines: ConsultantPayoutLineDto[];
}

export interface ConsultantPayoutSummaryRowDto {
  consultantId: string; code: string; name: string; specialty?: string | null;
  billCount: number; billed: number; share: number;
}

export const consultantsApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ConsultantDto>>("/api/v1/consultants", { params }).then(r => r.data),
  get: (id: string) => api.get<ConsultantDto>(`/api/v1/consultants/${id}`).then(r => r.data),
  create: (payload: UpsertConsultantRequest) =>
    api.post<{ id: string }>("/api/v1/consultants", payload).then(r => r.data),
  update: (id: string, payload: UpsertConsultantRequest) =>
    api.put(`/api/v1/consultants/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/consultants/${id}`),
  /** One consultant's statement. Reads what was recorded per line, not today's rules. */
  payout: (id: string, fromDate: string, toDate: string) =>
    api.get<ConsultantPayoutDto>(`/api/v1/consultants/${id}/payout`, {
      params: { fromDate, toDate },
    }).then(r => r.data),
  /** Everyone's totals for the period — the sheet a finance office pays from. */
  payouts: (fromDate: string, toDate: string) =>
    api.get<ConsultantPayoutSummaryRowDto[]>("/api/v1/consultants/payouts", {
      params: { fromDate, toDate },
    }).then(r => r.data),
};

// ---------- Schedule H1 supply register (W21) ----------

export interface ScheduleH1RegisterRow {
  suppliedAt: string;
  documentNo: string;
  patientName?: string | null; uhid?: string | null; patientPhone?: string | null;
  itemId: string; sku: string; drugName: string; scheduleCategory?: string | null;
  batchNo?: string | null; expiryDate?: string | null;
  qty: number;
  prescriberName?: string | null; prescriberRegNo?: string | null; prescriptionRef?: string | null;
  /** Who dispensed it — the register records who made the supply, not only that one was made. */
  dispensedBy?: string | null;
}

export const scheduleRegisterApi = {
  /**
   * The statutory H1 / X supply register. Distinct from the narcotic register under Inventory,
   * which projects stock movements and has never known a patient or a prescriber.
   */
  list: (params: {
    from?: string; to?: string; itemId?: string; scheduleCategory?: string;
  } = {}) =>
    api.get<ScheduleH1RegisterRow[]>("/api/v1/sales-invoices/schedule-register", { params })
      .then(r => r.data),
};
